// Enhanced Variant Pool - With State Management & Reset Validation
// Prevents the critical business risk of customers getting wrong pricing

class SimpleVariantPool {
  constructor() {
    // Debug mode
    this.debug = localStorage.getItem('poolDebug') === 'true';
    this.debugLog = (...args) => {
      if (this.debug) console.log(...args);
    };
    this.debugLog('🔍 EnhancedVariantPool initialized with debug mode');

    // Diagnostics only: most recent Netlify-confirmed write time per variant
    this.lastWriteConfirmedAt = {};

    // Telemetry system
    this.telemetry = {
      session: this.generateSessionId(),
      startTime: Date.now(),
      events: [],
      counters: {
        ghostDetections: 0,
        ghostRecoveries: 0,
        variantResets: 0,
        fallbacks: 0,
        successfulOrders: 0,
        failedOrders: 0,
        skuGenerationFailures: 0,
        cloudinaryUrls: 0
      }
    };

    // Initialize telemetry
    this.logTelemetryEvent('system_init', {
      debug: this.debug,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });

    // DEFENSIVE LAYER 1: Track recently reset variants (prevents CDN cache race conditions)
    // Variants reset within the last 90 seconds should be avoided to allow CDN propagation
    this.recentlyResetVariants = new Map(); // variantId -> resetTimestamp
    this.MIN_VARIANT_AGE_MS = 90000; // 90 seconds minimum age after reset

    // DEFENSIVE LAYER 2: Track variants currently being processed (prevents duplicate pool usage)
    // Prevents race condition where rapid double-add uses same pool variant for both items
    this.pendingVariants = new Set(); // variantIds currently in processOrder flow

    // Glass pool variant IDs (existing glass product)
    this.glassPoolVariants = [
      '63131022360963', '63131022393731', '63131022426499', '63131028750723', '63131028783491',
      '63131028816259', '63131028849027', '63131035369859', '63131035402627', '63131035435395',
      '63131035468163', '63131041792387', '63131041825155', '63131041857923', '63131041890691',
      '55883611210115', '63131048214915', '63131048280451', '63131048313219', '63131048477059',
      '63131048509827', '63131048542595', '63131054866819', '63131054899587', '63131054932355',
      '63131054965123', '63131054997891', '63131055030659', '63131055063427', '63131061420419',
      '63131061453187', '63131061485955', '63131061518723', '63131067842947', '63131067875715',
      '63131067908483', '63131067941251', '63131074265475', '63131074298243', '63131074331011',
      '63131080655235', '63131080688003', '63131080753539', '63131087077763', '63131087110531',
      '63131087143299', '63131093467523', '63131093500291', '63131093533059', '63131099857283'
    ];

    // Brick pool variant IDs (recreated with glass method for reliability)
    this.brickPoolVariants = [
      '56553509323139', // Brick Pool #1
      '56553509355907', // Brick Pool #2
      '56553509388675', // Brick Pool #3
      '56553509421443', // Brick Pool #4
      '56553509519747', // Brick Pool #5
      '56553509552515', // Brick Pool #6
      '56553509585283', // Brick Pool #7
      '56553509618051', // Brick Pool #8
      '56553509650819', // Brick Pool #9
      '56553509683587', // Brick Pool #10
      '56553509749123', // Brick Pool #11
      '56553509781891', // Brick Pool #12
      '56553509814659', // Brick Pool #13
      '56553509847427', // Brick Pool #14
      '56553509880195', // Brick Pool #15
      '56553509912963', // Brick Pool #16
      '56553510011267', // Brick Pool #17
      '56553510044035', // Brick Pool #18
      '56553510076803', // Brick Pool #19
      '56553510109571', // Brick Pool #20
      '56553510142339', // Brick Pool #21
      '56553510437251', // Brick Pool #22
      '56553510502787', // Brick Pool #23
      '56553510535555', // Brick Pool #24
      '56553510568323', // Brick Pool #25
      '56553510601091', // Brick Pool #26
      '56553510666627', // Brick Pool #27
      '56553510699395', // Brick Pool #28
      '56553510732163', // Brick Pool #29
      '56553510830467', // Brick Pool #30
      '56553510863235', // Brick Pool #31
      '56553510994307', // Brick Pool #32
      '56553511027075', // Brick Pool #33
      '56553511059843', // Brick Pool #34
      '56553511092611', // Brick Pool #35
      '56553511125379', // Brick Pool #36
      '56553511190915', // Brick Pool #37
      '56553511223683', // Brick Pool #38
      '56553511256451', // Brick Pool #39
      '56553511289219', // Brick Pool #40
      '56553511321987', // Brick Pool #42 (Pool #41 deleted)
      '56553511387523', // Brick Pool #43
      '56553511420291', // Brick Pool #44
      '56553511453059', // Brick Pool #45
      '56553511485827', // Brick Pool #46
      '56553511518595', // Brick Pool #47
      '56553511551363', // Brick Pool #48
      '56553511584131', // Brick Pool #49
      '56553511715203'  // Brick Pool #50
    ];

    // Set active pool based on product type
    this.poolVariants = this.glassPoolVariants;
    this.updateEndpoint = 'https://flourishing-cactus-81c4c1.netlify.app/.netlify/functions/enhanced-variant-updater';
    this.fallbackToCart = true;
    this.productType = 'glass';
    this.SINGLE_FLIGHT_LOCK_KEY = '__sgr_calculator_order_lock';
    this.SINGLE_FLIGHT_TTL_MS = 45000;

    // Cart cache to prevent API hammering (Throttling)
    this.cartCache = {
      data: null,
      timestamp: 0,
      promise: null,
      TTL: 2500 // 2.5 seconds cache
    };

    // Initialize abandoned cart cleanup system
    this.initializeCleanup();

    // Variant states
    this.STATES = {
      CLEAN: 'CLEAN',           // Ready to use
      IN_USE: 'IN_USE',         // Currently assigned to customer
      PENDING_CLEANUP: 'PENDING_CLEANUP', // Being reset
      QUARANTINED: 'QUARANTINED' // Failed reset, needs manual intervention
    };

    if (this.debug) {
      this.debugLog('🎯 Enhanced pool configured with', this.poolVariants.length, 'variants');
      this.debugLog('📡 Endpoint:', this.updateEndpoint);
    }
  }

  acquireSingleFlightLock(productType = 'glass') {
    const now = Date.now();
    const existingLock = window[this.SINGLE_FLIGHT_LOCK_KEY];

    if (existingLock && existingLock.active && existingLock.expiresAt > now) {
      throw new Error('ORDER_IN_FLIGHT');
    }

    const owner = `${productType}_${now}_${Math.random().toString(36).slice(2, 8)}`;
    window[this.SINGLE_FLIGHT_LOCK_KEY] = {
      active: true,
      owner: owner,
      startedAt: now,
      expiresAt: now + this.SINGLE_FLIGHT_TTL_MS,
      productType: productType
    };

    return owner;
  }

  releaseSingleFlightLock(owner) {
    if (!owner) return;
    const existingLock = window[this.SINGLE_FLIGHT_LOCK_KEY];
    if (existingLock && existingLock.owner === owner) {
      window[this.SINGLE_FLIGHT_LOCK_KEY] = {
        active: false,
        owner: null,
        startedAt: 0,
        expiresAt: 0,
        productType: null
      };
    }
  }

  // ==================== PRICING VALIDATION ====================

  /**
   * Validate pricing against business rules to prevent revenue loss
   * @param {number} price - The calculated price per item
   * @param {string} thickness - The glass/brick thickness (e.g., '4mm')
   * @param {object} dimensions - Dimensions for context
   */
  validatePricing(price, thickness, dimensions) {
    // Minimum price rules per thickness (Price Per Item)
    // Reduced to £8 minimum to avoid blocking legitimate small orders
    const MIN_PRICES = {
      '3mm': 8.00,
      '4mm': 8.00,
      '5mm': 8.00,
      '6mm': 8.00
    };

    // 1. Check minimum price
    // Bricks are much cheaper than glass, so we use a lower minimum for bricks
    const isBrick = this.productType === 'brick';
    const minPrice = isBrick ? 1.00 : (MIN_PRICES[thickness] || 8.00);

    if (price < minPrice - 0.01) {
      console.error(`⚠️ Price validation failed: £${price.toFixed(2)} is below minimum £${minPrice.toFixed(2)} (${isBrick ? 'brick' : thickness})`);
      throw new Error(`Price £${price.toFixed(2)} is below minimum £${minPrice.toFixed(2)}`);
    }

    // 2. Check for zero or negative price
    if (price <= 0.01) {
      throw new Error(`Invalid price £${price.toFixed(2)} detected`);
    }

    // 3. Sanity check for extremely high prices (prevent accidental huge charges)
    // Warn if over £1000 for a single piece (unlikely for stove glass)
    if (price > 1000) {
      console.warn(`⚠️ High price warning: £${price.toFixed(2)} for single item`);
      // We don't block this, just warn, as large orders are possible
    }

    if (this.debug) {
      this.debugLog(`✅ Pricing validated: £${price.toFixed(2)} for ${thickness}`);
    }

    return true;
  }

  // ==================== TELEMETRY SYSTEM ====================

  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  logTelemetryEvent(eventType, data = {}) {
    const event = {
      type: eventType,
      timestamp: Date.now(),
      sessionId: this.telemetry.session,
      data: data
    };

    this.telemetry.events.push(event);

    // Keep only last 100 events to prevent memory bloat
    if (this.telemetry.events.length > 100) {
      this.telemetry.events = this.telemetry.events.slice(-100);
    }

    // Structured logging for external monitoring
    console.log('POOL_TELEMETRY:', JSON.stringify(event, null, 2));

    this.debugLog('📊 Telemetry event:', eventType, data);

    // Send important events to dashboard automatically
    const importantEvents = ['order_success', 'order_failure', 'ghost_detection_start', 'cloudinary_sku_used', 'variant_reset_success'];
    if (importantEvents.includes(eventType)) {
      this.sendToAnalyticsDashboard({
        eventType: eventType,
        sessionId: this.telemetry.session,
        timestamp: event.timestamp,
        counters: { ...this.telemetry.counters },
        data: data
      });
    }

    // Lightweight in-browser monitoring hook for operational visibility.
    if (eventType === 'order_failure') {
      this.emitMonitoringHook('process_order_failure', data);
    }
    if (eventType === 'fallback_used') {
      this.emitMonitoringHook('fallback_used', data);
    }
  }

  emitMonitoringHook(metricName, data = {}) {
    try {
      const storageKey = 'calculator_monitoring_metrics';
      const current = JSON.parse(localStorage.getItem(storageKey) || '{}');
      current[metricName] = (current[metricName] || 0) + 1;
      current.lastUpdated = Date.now();
      localStorage.setItem(storageKey, JSON.stringify(current));

      document.dispatchEvent(new CustomEvent('calculator:monitoring', {
        detail: {
          metric: metricName,
          count: current[metricName],
          timestamp: current.lastUpdated,
          data: data
        }
      }));
    } catch (error) {
      this.debugLog('Monitoring hook error:', error);
    }
  }

  incrementCounter(counterName, additionalData = {}) {
    if (this.telemetry.counters.hasOwnProperty(counterName)) {
      this.telemetry.counters[counterName]++;
      this.logTelemetryEvent('counter_increment', {
        counter: counterName,
        newValue: this.telemetry.counters[counterName],
        ...additionalData
      });
    }
  }

  getTelemetrySummary() {
    const sessionDuration = Date.now() - this.telemetry.startTime;
    return {
      sessionId: this.telemetry.session,
      sessionDuration: sessionDuration,
      counters: { ...this.telemetry.counters },
      recentEvents: this.telemetry.events.slice(-10),
      timestamp: new Date().toISOString()
    };
  }

  sendTelemetryBeacon(eventType, data = {}) {
    const telemetryData = {
      eventType: eventType,
      sessionId: this.telemetry.session,
      timestamp: Date.now(),
      counters: { ...this.telemetry.counters },
      data: data
    };

    // Send to analytics dashboard
    this.sendToAnalyticsDashboard(telemetryData);

    // Use beacon API for reliable transmission
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(telemetryData)], { type: 'application/json' });
      navigator.sendBeacon('/analytics/telemetry', blob);
    }

    // Fallback structured logging
    console.log('TELEMETRY_BEACON:', JSON.stringify(telemetryData, null, 2));
  }

  // Send telemetry to analytics dashboard
  async sendToAnalyticsDashboard(telemetryData) {
    try {
      const response = await fetch('https://flourishing-cactus-81c4c1.netlify.app/.netlify/functions/enhanced-analytics-accurate?endpoint=telemetry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(telemetryData)
      });

      if (response.ok) {
        this.debugLog('📊 Telemetry sent to dashboard successfully');
      } else {
        this.debugLog('⚠️ Failed to send telemetry to dashboard:', response.status);
      }
    } catch (error) {
      this.debugLog('⚠️ Telemetry dashboard error:', error);
      // Don't throw - telemetry shouldn't break main functionality
    }
  }

  // Get variant state from localStorage
  getVariantState(variantId) {
    const stateKey = `variant_state_${variantId}`;
    return localStorage.getItem(stateKey) || this.STATES.CLEAN;
  }

  // Set variant state in localStorage (legacy method, now uses timestamps)
  setVariantState(variantId, state) {
    this.setVariantStateWithTimestamp(variantId, state);
  }

  // Get default clean state for a variant
  getDefaultVariantState(variantId, productType = 'glass') {
    const poolIndex = this.poolVariants.indexOf(variantId) + 1;
    const prefix = productType === 'brick' ? 'Brick' : 'Glass';

    return {
      title: `Custom ${prefix} Pool #${poolIndex}`,
      price: '0.01',
      sku: `POOL-${poolIndex.toString().padStart(3, '0')}`
    };
  }

  // Set variant state with timestamp for abandoned cart detection
  setVariantStateWithTimestamp(variantId, state) {
    const stateKey = `variant_state_${variantId}`;
    const timestampKey = `variant_timestamp_${variantId}`;

    localStorage.setItem(stateKey, state);
    localStorage.setItem(timestampKey, Date.now().toString());

    if (this.debug) {
      this.debugLog(`🏷️ Variant ${variantId} state: ${state} (with timestamp)`);
    }
  }

  // Get variant state with timestamp
  getVariantStateWithTimestamp(variantId) {
    const stateKey = `variant_state_${variantId}`;
    const timestampKey = `variant_timestamp_${variantId}`;

    const state = localStorage.getItem(stateKey) || this.STATES.CLEAN;
    const timestamp = localStorage.getItem(timestampKey);

    return {
      state: state,
      timestamp: timestamp ? parseInt(timestamp) : Date.now()
    };
  }

  // Initialize cleanup system - runs periodically to prevent abandoned cart buildup
  initializeCleanup() {
    // Detect if we're on the cart page.
    // IMPORTANT: cart contamination cleanup mutates cart state; keep it cart-page only
    // to avoid collisions with active add-to-cart actions on product/collection pages.
    const isCartPage = window.location.pathname.includes('/cart');

    if (isCartPage) {
      // Cart page: Run cleanup IMMEDIATELY (no delay) - must catch contaminated items before checkout
      this.debugLog('🛒 Cart page detected - running immediate contamination cleanup');
      this.cleanupContaminatedCartItems().then((removedCount) => {
        if (removedCount > 0) {
          this.debugLog(`✅ Removed ${removedCount} items on cart page - forcing reload`);
          // Session flag so the post-reload load shows the banner exactly once,
          // tied to THIS removal. Without this, 7-day snapshots reappear on
          // every clean cart visit and cry wolf on healthy carts.
          try { sessionStorage.setItem('sgr_just_cleaned', String(Date.now())); } catch (e) {}
          window.location.reload();
        } else {
          this.debugLog('✅ Cart page cleanup complete - no items removed');
          // Only show the banner when THIS session just removed something.
          // Stale snapshots from earlier sessions stay in storage but silent.
          try {
            const justCleaned = parseInt(sessionStorage.getItem('sgr_just_cleaned') || '0', 10) || 0;
            if (justCleaned && (Date.now() - justCleaned) < 10 * 60 * 1000) {
              sessionStorage.removeItem('sgr_just_cleaned');
              this.renderExpiredRecoveryBanner();
            }
          } catch (e) {}
        }
      });
    }

    // Run variant cleanup after a delay
    setTimeout(() => this.cleanupAbandonedVariants(), 2000);

    // Set up periodic cleanup every 5 minutes.
    // Contaminated cart cleanup is cart-page only.
    if (isCartPage) {
      setInterval(() => this.cleanupContaminatedCartItems(), 5 * 60 * 1000);
    }
    setInterval(() => this.cleanupAbandonedVariants(), 5 * 60 * 1000);

    this.debugLog('🧹 Abandoned cart cleanup system initialized');
  }

  // NEW: Cart Item Cleanup - Remove pool variants with £0.01 pricing from customer's cart
  // This prevents contaminated items from previous sessions from being ordered
  // Returns number of items removed
  async cleanupContaminatedCartItems() {
    try {
      this.debugLog('🛒 Starting cart contamination cleanup...');

      // Fetch current cart using throttled method
      const cart = await this.getThrottledCart();
      if (!cart.items || cart.items.length === 0) {
        this.debugLog('🛒 Cart is empty, no cleanup needed');
        return 0;
      }

      // Combine all pool variants for checking
      const allPoolVariants = [...this.glassPoolVariants, ...this.brickPoolVariants];

      // Find contaminated items (pool variants with suspicious pricing/SKU)
      // Covers: nightly 3AM reset, multi-day stale carts, and cross-customer overwrite
      // (another buyer reuses the same pool variant and overwrites price/SKU).
      const contaminatedItems = cart.items.filter(item => {
        const variantIdStr = item.variant_id.toString();
        const isPoolVariant = allPoolVariants.includes(variantIdStr);

        if (!isPoolVariant) return false;

        const props = item.properties || {};
        const priceInPounds = item.price / 100;
        const isSuspiciousPrice = priceInPounds === 0.01 || priceInPounds < 1;
        const sku = item.sku != null ? String(item.sku) : '';
        const isSuspiciousSKU = sku === '' || sku.startsWith('POOL-') || sku.startsWith('BRICK-POOL-');

        // Shopify's storefront variant cache can keep returning the PREVIOUS
        // customer's price/SKU for minutes after the Admin API write (confirmed
        // in production). A line added by the current checkout must never be
        // treated as abandoned - even if this cart read looks stale.
        const addedAt = parseInt(props['_Added At'] || props['_Added at'] || '0', 10);
        const isFresh = Number.isFinite(addedAt) && addedAt > 0 &&
          (Date.now() - addedAt) < 15 * 60 * 1000;

        // Expired multi-day cart: variant was reset/reused after add, so the live
        // price no longer matches the price quoted at add time (_Expected Price).
        // DISCOUNT-AWARE: item.price is the discounted unit price. A BOGO/offer
        // split line must NOT be mistaken for expiry - compare the pre-discount
        // original_price too; either matching means the line is fresh.
        let expectedPriceMismatch = false;
        try {
          const expected = props['_Expected Price'] || props['_Expected price'];
          if (expected !== undefined && expected !== null && expected !== '') {
            const expectedNum = parseFloat(expected);
            if (Number.isFinite(expectedNum)) {
              const origInPounds = (item.original_price != null ? item.original_price : item.price) / 100;
              expectedPriceMismatch = Math.abs(priceInPounds - expectedNum) > 0.01 &&
                Math.abs(origInPounds - expectedNum) > 0.01;
            }
          }
        } catch (e) { /* ignore - treat as not mismatched */ }

        if (isFresh) {
          this.debugLog('⏭️ Skipping recently added pool line (storefront cache grace)', {
            variantId: variantIdStr,
            sku: sku,
            price: priceInPounds,
            ageSeconds: Math.round((Date.now() - addedAt) / 1000)
          });
          return false;
        }

        // SKU-only lag with a correct price is a CDN artefact, not a bad order.
        // Price is the checkout-critical field; a later cart visit will still
        // catch genuine cross-customer reuse via _Expected Price.
        if (isSuspiciousSKU && !isSuspiciousPrice && !expectedPriceMismatch) {
          this.debugLog('⏭️ Skipping SKU-only pool lag (price is valid)', {
            variantId: variantIdStr,
            sku: sku,
            price: priceInPounds
          });
          return false;
        }

        return isSuspiciousPrice || expectedPriceMismatch;
      });

      if (contaminatedItems.length === 0) {
        this.debugLog('✅ No contaminated pool items found in cart');
        return 0;
      }

      this.debugLog(`🚨 Found ${contaminatedItems.length} contaminated pool items in cart`, contaminatedItems);

      // Preserve sizing spec BEFORE removal so the customer is not sent back to
      // square one. Recovery survives the reload via localStorage.
      this.saveExpiredRecovery(contaminatedItems);

      // Remove each contaminated item
      for (const item of contaminatedItems) {
        try {
          this.debugLog(`🗑️ Removing contaminated item: ${item.sku} @ £${(item.price / 100).toFixed(2)}`, {
            key: item.key,
            variant_id: item.variant_id,
            sku: item.sku,
            price: item.price
          });

          const removeResponse = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: item.key,
              quantity: 0
            })
          });

          if (removeResponse.ok) {
            this.debugLog(`✅ Removed contaminated item ${item.sku}`);
            this.logTelemetryEvent('cart_contamination_cleanup', {
              variantId: item.variant_id.toString(),
              sku: item.sku,
              price: item.price / 100,
              removed: true
            });
          } else {
            this.debugLog(`❌ Failed to remove contaminated item ${item.sku}`);
          }

          // Small delay between removals
          await new Promise(resolve => setTimeout(resolve, 100));

        } catch (err) {
          this.debugLog(`❌ Error removing contaminated item:`, err);
        }
      }

      this.debugLog(`✅ Cart contamination cleanup complete. Removed ${contaminatedItems.length} items.`);

      // Show a brief notification if items were removed (optional - may want to add UI feedback)
      if (contaminatedItems.length > 0) {
        console.warn(`⚠️ Removed ${contaminatedItems.length} invalid items from your cart. Your sizes were saved - see recovery banner after reload.`);
      }

      return contaminatedItems.length;

    } catch (error) {
      this.debugLog('❌ Cart contamination cleanup error:', error);
      return 0;
    }
  }

  // ==================== EXPIRED-CART RECOVERY ====================
  // Multi-day carts die when the shared pool variant is reset (3AM cleanup) or
  // reused by another buyer. Instead of silent removal, preserve the sizing
  // spec in localStorage so the customer gets 1-click restore, not square one.
  // Recovery keys (shared with calculator logic files):
  //   sgr_expired_recovery_v1 - array of snapshots {id, variantId, sku, price, quantity, properties, productType, timestamp, restored, dismissed}
  //   sgr_restore_request_v1  - single snapshot the calculator should prefill
  getRecoveryStore() {
    try {
      return JSON.parse(localStorage.getItem('sgr_expired_recovery_v1') || '[]');
    } catch (e) { return []; }
  }

  setRecoveryStore(list) {
    try {
      localStorage.setItem('sgr_expired_recovery_v1', JSON.stringify((list || []).slice(0, 5)));
    } catch (e) { /* storage full/blocked - non-critical */ }
  }

  detectProductTypeForVariant(variantIdStr) {
    if ((this.brickPoolVariants || []).includes(variantIdStr)) return 'brick';
    return 'glass';
  }

  saveExpiredRecovery(contaminatedItems) {
    try {
      const store = this.getRecoveryStore();
      const now = Date.now();
      for (const item of contaminatedItems) {
        const props = item.properties || {};
        const qty = parseInt(props['_Quantity'] || props['Quantity'] || item.quantity || 1, 10) || 1;
        const snapshot = {
          id: `${now}-${item.variant_id}`,
          variantId: item.variant_id.toString(),
          sku: item.sku || '',
          price: item.price / 100,
          quantity: qty,
          properties: props,
          productType: this.detectProductTypeForVariant(item.variant_id.toString()),
          timestamp: now,
          restored: false,
          dismissed: false
        };
        // Avoid duplicate snapshots for the same variant+spec within 1 hour
        const dupe = store.some(s => s.variantId === snapshot.variantId &&
          JSON.stringify(s.properties) === JSON.stringify(snapshot.properties) &&
          (now - s.timestamp) < 3600000);
        if (!dupe) store.unshift(snapshot);
        this.logTelemetryEvent('expired_recovery_saved', {
          variantId: snapshot.variantId,
          productType: snapshot.productType,
          quantity: qty
        });
      }
      this.setRecoveryStore(store);
    } catch (e) {
      this.debugLog('⚠️ Failed to save expired recovery:', e);
    }
  }

  summarizeRecoverySnapshot(snap) {
    try {
      const p = snap.properties || {};
      const pick = (keys) => {
        for (const k of keys) {
          if (p[k] !== undefined && p[k] !== null && String(p[k]).trim() !== '') return String(p[k]);
        }
        const found = Object.keys(p).find(k => keys.some(w => k.toLowerCase().includes(w.toLowerCase())));
        return found ? String(p[found]) : '';
      };
      const glassType = p['Glass Type'] || p['Brick Type'] || (snap.productType === 'brick' ? 'Custom brick' : 'Custom glass');
      const w = pick(['Overall Width', 'Width', 'Bottom Width', 'Diameter']);
      const h = pick(['Overall Height', 'Height', 'Bottom Width']);
      const dims = [w, h].filter(Boolean).join(' × ') || 'saved sizes';
      const qty = snap.quantity || 1;
      return `${glassType} — ${dims} (Qty: ${qty})`;
    } catch (e) { return 'your saved custom sizes'; }
  }

  renderExpiredRecoveryBanner() {
    try {
      if (document.getElementById('sgr-recovery-banner')) return;
      const pending = this.getRecoveryStore().filter(s => !s.restored && !s.dismissed && (Date.now() - s.timestamp) < 7 * 24 * 3600 * 1000);
      if (pending.length === 0) return;
      const banner = document.createElement('div');
      banner.id = 'sgr-recovery-banner';
      banner.setAttribute('role', 'alert');
      // Theme-matched styling: same bar pattern as sgr-nudge (warm tint, 4px
      // radius), dark button like cart checkout, underline dismiss like the
      // locked-row Remove buttons. Flex wraps on mobile (44px tap targets).
      const style = document.createElement('style');
      style.textContent = '.sgr-recovery{margin:0 0 14px;padding:12px;background:#fff8f3;border:1px solid #f0d9c2;border-radius:4px;font-size:14px;color:#333;line-height:1.35;}' +
        '.sgr-recovery-text{margin:0 0 8px;}.sgr-recovery-text strong{display:block;font-size:16px;font-weight:700;margin-bottom:2px;}.sgr-recovery-text span{display:block;font-size:14px;color:#555;}' +
        '.sgr-recovery-items{margin:0;padding:0;list-style:none;display:flex;flex-direction:column;gap:8px;}' +
        '.sgr-recovery-items li{display:flex;flex-wrap:wrap;align-items:center;gap:8px 10px;padding-top:8px;border-top:1px solid #f0d9c2;}' +
        '.sgr-recovery-items li:first-child{padding-top:0;border-top:0;}' +
        '.sgr-recovery-more{font-size:13px;color:#555;padding-top:6px;border-top:1px solid #f0d9c2;}' +
        '.sgr-recovery-spec{flex:1 1 180px;min-width:0;font-size:14px;}' +
        '.sgr-recovery-add{background:#e56c03;color:#fff;border:1px solid #e56c03;border-radius:2px;min-height:48px;padding:0 18px;font-size:15px;font-weight:600;cursor:pointer;white-space:nowrap;}' +
        /* Quiet look, full 44px tap target (was ~20px). */
        '.sgr-recovery-dismiss{background:none;border:0;color:#555;font-size:13px;text-decoration:underline;cursor:pointer;display:inline-flex;align-items:center;min-height:44px;min-width:44px;padding:0 8px;white-space:nowrap;}' +
        '@media (max-width:767px){.sgr-recovery-items li{gap:8px;}.sgr-recovery-add{flex:1 1 auto;}.sgr-recovery-dismiss{margin-left:auto;}}';
      banner.appendChild(style);
      const esc = (v) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const wrap = document.createElement('div');
      wrap.className = 'sgr-recovery';
      const SHOWN_MAX = 3;
      const itemsHtml = pending.slice(0, SHOWN_MAX).map(s => {
        const summary = esc(this.summarizeRecoverySnapshot(s));
        const cta = s.productType === 'brick' ? 'Re-add my bricks' : 'Re-add my glass';
        return `<li><span class="sgr-recovery-spec">${summary}</span>` +
          `<button type="button" class="sgr-recovery-add" data-sgr-restore="${esc(s.id)}">${cta}</button>` +
          `<button type="button" class="sgr-recovery-dismiss" data-sgr-dismiss="${esc(s.id)}">Dismiss</button></li>`;
      }).join('');
      const overflow = pending.length - SHOWN_MAX;
      const overflowHtml = overflow > 0 ? `<div class="sgr-recovery-more">and ${overflow} more</div>` : '';
      /* Headline must be grammatical for EVERY pending item, not just the first:
         "glass was" is a mass noun; brick-only must say "brick was" for one item
         and "bricks were" for several (never "bricks was"); mixed types fall
         back to "items were". The sub-line follows the count. */
      const allBricks = pending.length > 0 && pending.every(s => s.productType === 'brick');
      const allGlass = pending.length > 0 && pending.every(s => s.productType !== 'brick');
      const many = pending.length > 1;
      const noun = allGlass ? 'glass' : (allBricks ? (many ? 'bricks' : 'brick') : 'items');
      const verb = (allGlass || !many) ? 'was' : 'were';
      const sub = many
        ? 'Your sizes are saved. Add them back in one tap.'
        : 'Your size is saved. Add it back in one tap.';
      wrap.innerHTML = `<div class="sgr-recovery-text"><strong>Your cut-to-size ${noun} ${verb} removed from your cart.</strong>` +
        `<span>${sub}</span></div>` +
        `<ul class="sgr-recovery-items">${itemsHtml}</ul>${overflowHtml}`;
      banner.appendChild(wrap);
      /* Anchor below the site header, inside the page content. The old chain
         fell back to `body`, which inserted this ABOVE the header (pushing the
         logo/menu ~1360px down and hiding the cart on arrival).
         IMPORTANT: `checkout-cart` is NOT unique -- the HEADER mini-cart's
         checkout links carry it too (widget-shopping-cart.liquid), and they sit
         earlier in the DOM. A bare `.checkout-cart` query matched that header
         link and injected the banner INSIDE it (invisible, and its buttons were
         nested in an anchor). Scope to the cart page's own section first, then
         page-content wrappers, then <body> (insert AFTER the header, not
         first-child). `.cart-container`/`.cart-main` never exist in this theme. */
      const cartEl = document.querySelector('[data-section-type="cart-template"] .checkout-cart')
        || document.querySelector('.checkout-cart[role="main"]')
        || document.querySelector('.main-wrapper .checkout-cart');
      let anchor = cartEl;
      if (!anchor) {
        const wrapper = document.querySelector('.main-wrapper .main, .main-wrapper, main');
        if (wrapper) {
          anchor = wrapper;
          banner.classList.add('sgr-recovery-inpage');
          banner.style.margin = '0 0 14px';
          banner.style.width = '100%';
        }
      }
      if (!anchor) { anchor = document.body; banner.classList.add('sgr-recovery-inpage'); }
      if (!anchor) return;
      const headerEl = document.querySelector('.header-container, header.header, .main-header');
      if (anchor === document.body && headerEl && headerEl.parentNode === anchor) {
        anchor.insertBefore(banner, headerEl.nextSibling);
      } else {
        anchor.insertBefore(banner, anchor.firstChild);
      }
      banner.addEventListener('click', (e) => {
        const restoreBtn = e.target.closest('[data-sgr-restore]');
        const dismissBtn = e.target.closest('[data-sgr-dismiss]');
        if (restoreBtn) {
          const id = restoreBtn.getAttribute('data-sgr-restore');
          const store = this.getRecoveryStore();
          const snap = store.find(s => s.id === id);
          if (snap) {
            try { localStorage.setItem('sgr_restore_request_v1', JSON.stringify(snap)); } catch (err) {}
            snap.restored = true;
            this.setRecoveryStore(store);
            this.logTelemetryEvent('expired_recovery_restore_clicked', { variantId: snap.variantId });
            const url = snap.productType === 'brick'
              ? '/products/replacement-stove-bricks-cut-to-size-vermiculite-15mm-20mm-and-25mm-thick'
              : '/products/cut-to-size-stove-glass';
            window.location.href = url;
          }
        } else if (dismissBtn) {
          const id = dismissBtn.getAttribute('data-sgr-dismiss');
          const store = this.getRecoveryStore();
          const snap = store.find(s => s.id === id);
          if (snap) { snap.dismissed = true; this.setRecoveryStore(store); }
          banner.remove();
          // Re-render so dismissing one item doesn't hide the others.
          const remaining = this.getRecoveryStore().filter(s => !s.restored && !s.dismissed && (Date.now() - s.timestamp) < 7 * 24 * 3600 * 1000);
          if (remaining.length > 0) this.renderExpiredRecoveryBanner();
        }
      });
    } catch (e) {
      this.debugLog('⚠️ Failed to render recovery banner:', e);
    }
  }

  // Abandoned Cart Cleanup - Reset variants stuck in non-clean states
  async cleanupAbandonedVariants() {
    try {
      this.debugLog('🧹 Starting abandoned cart cleanup...');

      const ABANDON_TIMEOUT = 15 * 60 * 1000; // 15 minutes
      const now = Date.now();
      let cleanedCount = 0;

      // Never reset a variant that is still in this cart: checkout charges the
      // variant's live price, so a reset would bill the customer £0.01. If the
      // cart can't be read, skip this round rather than risk it.
      let cartVariantIds;
      try {
        const cart = await this.getThrottledCart();
        // The theme's fetch wrapper returns a synthetic empty cart (no token)
        // during a 429 backoff; treat that as unreadable.
        if (!cart || !cart.token || !Array.isArray(cart.items)) throw new Error('Cart not readable');
        cartVariantIds = new Set(cart.items.map(item => String(item.variant_id)));
      } catch (cartError) {
        this.debugLog('⚠️ Abandoned cleanup skipped - cart unavailable:', cartError);
        return;
      }

      for (const variantId of this.poolVariants) {
        const { state, timestamp } = this.getVariantStateWithTimestamp(variantId);

        // Skip variants that are already clean
        if (state === this.STATES.CLEAN) continue;

        // Still reserved by an item in this cart - not abandoned
        if (cartVariantIds.has(String(variantId))) continue;

        // Check if variant has been stuck for too long
        const stuckTime = now - timestamp;

        if (stuckTime > ABANDON_TIMEOUT) {
          this.debugLog(`🚨 Found abandoned variant ${variantId}, stuck for ${Math.round(stuckTime / 1000 / 60)} minutes`);

          try {
            // Reset to clean state
            await this.resetVariant(variantId);
            cleanedCount++;

            // Small delay between resets to avoid rate limiting
            await new Promise(resolve => setTimeout(resolve, 100));

          } catch (error) {
            this.debugLog(`❌ Failed to cleanup variant ${variantId}:`, error);
          }
        }
      }

      this.debugLog(`✅ Abandoned cart cleanup complete. Cleaned ${cleanedCount} variants.`);

    } catch (error) {
      this.debugLog('❌ Abandoned cart cleanup error:', error);
    }
  }

  // Manual cleanup trigger for testing and emergency situations
  async forceCleanupAllVariants() {
    this.debugLog('🚨 FORCE CLEANUP: Resetting ALL variants regardless of state...');

    let cleanedCount = 0;
    let errorCount = 0;

    for (const variantId of this.poolVariants) {
      try {
        this.debugLog(`🔄 Force resetting variant ${variantId}...`);
        await this.resetVariant(variantId);
        cleanedCount++;

        // Clear local state regardless of server success
        localStorage.removeItem(`variant_state_${variantId}`);
        localStorage.removeItem(`variant_timestamp_${variantId}`);

        // Delay between resets to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 200));

      } catch (error) {
        errorCount++;
        this.debugLog(`❌ Failed to force reset variant ${variantId}:`, error);

        // Clear local state even if server reset failed
        localStorage.removeItem(`variant_state_${variantId}`);
        localStorage.removeItem(`variant_timestamp_${variantId}`);
      }
    }

    this.debugLog(`✅ Force cleanup complete. Reset ${cleanedCount} variants, ${errorCount} errors.`);
    this.debugLog('🧹 All local variant states cleared.');
    return { cleaned: cleanedCount, errors: errorCount };
  }

  // Ghost Data Detection - Catches abandoned cart data inheritance
  async detectGhostData(variantId, expectedPrice, quantity, cleanProperties, productType, fullCalculatorData = null) {
    try {
      this.debugLog('👻 Starting ghost data detection...');
      this.logTelemetryEvent('ghost_detection_start', {
        variantId: variantId,
        expectedPrice: expectedPrice,
        productType: productType
      });

      // POLLING STRATEGY: Check twice to handle slow Shopify API updates
      // First check: 300ms (fast path)
      // Second check: 800ms (slow path - if first check fails or is inconclusive)

      const checkCart = async (attemptName) => {
        if (this.debug) this.debugLog(`👻 Ghost detection check: ${attemptName}`);
        return await this.getThrottledCart();
      };

      // Wait for initial propagation
      await new Promise(resolve => setTimeout(resolve, 300));

      let cart = await checkCart('initial_300ms');

      // Handle Shopify discount splits - item may be split into multiple line items
      // Sum up total quantity for this variant across all cart items
      let addedItems = cart.items.filter(item => item.variant_id.toString() === variantId.toString());
      let totalQuantityInCart = addedItems.reduce((sum, item) => sum + item.quantity, 0);
      let addedItem = addedItems[0]; // Use first item for price/SKU check

      // If item not found, wait longer and try again (Shopify might be slow)
      if (!addedItem) {
        this.debugLog('⚠️ Item not found in cart on first check, waiting longer...');
        await new Promise(resolve => setTimeout(resolve, 500)); // Wait another 500ms (total 800ms)

        cart = await checkCart('retry_800ms');
        addedItems = cart.items.filter(item => item.variant_id.toString() === variantId.toString());
        totalQuantityInCart = addedItems.reduce((sum, item) => sum + item.quantity, 0);
        addedItem = addedItems[0];
      }


      if (!addedItem) {
        this.debugLog('⚠️ Item not found in cart for ghost detection');
        return;
      }

      this.debugLog('🔍 Checking for ghost data...', {
        expectedPrice: expectedPrice,
        actualPrice: addedItem.price,
        actualPriceInPounds: (addedItem.price / 100),
        actualSKU: addedItem.sku,
        variantId: variantId,
        fullCalculatorData: fullCalculatorData
      });

      // Check for price mismatch (ghost data from abandoned cart)
      const actualPriceInPounds = addedItem.price / 100;
      const priceDifference = expectedPrice ? Math.abs(actualPriceInPounds - expectedPrice) : 0;
      const priceMismatch = expectedPrice && priceDifference > 0.01;

      this.debugLog('💰 Price comparison:', {
        expectedPrice: expectedPrice,
        actualPriceInPounds: actualPriceInPounds,
        priceDifference: priceDifference,
        priceMismatch: priceMismatch
      });

      // Check for suspicious SKU patterns (ghost data indicators)
      // Only flag as suspicious if it doesn't match what we just generated
      const poolSKUPattern = /^POOL-\d{3}$/; // Expected clean pool SKU
      const suspiciousKU = false; // Disable pattern detection since exact SKU comparison is more reliable

      // Exact SKU verification - catches ALL ghost data scenarios
      const expectedSKU = fullCalculatorData ? this.generateSKU(fullCalculatorData) : null;

      // Defensive: if SKU generation failed, skip SKU mismatch check
      const skuMismatch = expectedSKU && addedItem.sku !== expectedSKU;

      // Debug shaped glass SKU generation
      if (fullCalculatorData && fullCalculatorData.shape !== 'square-rectangular') {
        this.debugLog('🔧 Shaped glass debug:', {
          shape: fullCalculatorData.shape,
          fullCalculatorData: fullCalculatorData,
          expectedSKU: expectedSKU,
          actualSKU: addedItem.sku,
          skuMismatch: skuMismatch
        });
      }

      this.debugLog('🏷️ SKU comparison:', {
        expectedSKU: expectedSKU,
        actualSKU: addedItem.sku,
        skuMismatch: skuMismatch,
        fullCalculatorDataExists: !!fullCalculatorData,
        generateSKUInput: fullCalculatorData
      });

      this.debugLog('🔍 Ghost detection summary:', {
        priceMismatch: priceMismatch,
        suspiciousKU: suspiciousKU,
        skuMismatch: skuMismatch,
        willTriggerGhostDetection: !!(priceMismatch || suspiciousKU || skuMismatch)
      });

      if (priceMismatch || suspiciousKU || skuMismatch) {
        this.debugLog('🚨 GHOST DATA DETECTED!', {
          priceMismatch: priceMismatch,
          suspiciousKU: suspiciousKU,
          skuMismatch: skuMismatch,
          expectedSKU: expectedSKU,
          actualSKU: addedItem.sku,
          action: 'Removing contaminated item and retrying with fresh variant'
        });

        // Track ghost detection in telemetry
        this.incrementCounter('ghostDetections', {
          variantId: variantId,
          reasons: {
            priceMismatch: priceMismatch,
            suspiciousKU: suspiciousKU,
            skuMismatch: skuMismatch
          },
          actualPrice: actualPriceInPounds,
          expectedPrice: expectedPrice,
          actualSKU: addedItem.sku,
          expectedSKU: expectedSKU
        });

        // Remove contaminated item from cart
        this.debugLog('🗑️ Removing contaminated item:', { id: addedItem.id, key: addedItem.key });

        const removeResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: addedItem.key, // Use key instead of id for cart removal
            quantity: 0
          })
        });

        if (!removeResponse.ok) {
          const errorText = await removeResponse.text();
          this.debugLog('❌ Failed to remove contaminated item:', {
            status: removeResponse.status,
            error: errorText,
            itemData: addedItem
          });
          // Continue anyway - better to have 2 items than infinite loop
        } else {
          this.debugLog('✅ Successfully removed contaminated item');
          this.incrementCounter('ghostRecoveries', {
            variantId: variantId,
            recoveryMethod: 'cart_item_removal'
          });
        }

        // Quarantine the contaminated variant
        this.setVariantState(variantId, this.STATES.QUARANTINED);

        // Automatically retry with a fresh variant
        this.debugLog('🔄 Auto-retrying with fresh variant...');

        // Find a different clean variant
        const freshVariantId = await this.findAvailableVariant();

        if (freshVariantId && freshVariantId !== variantId) {
          // Update the fresh variant with correct details (use fullCalculatorData, not cleanProperties)
          const title = productType === 'brick' ? 'Custom Cut Brick' : 'Custom Cut Glass';
          const sku = this.generateSKU(fullCalculatorData);

          // Don't update if SKU generation failed - keep the variant clean
          if (sku === null) {
            this.debugLog('⚠️ SKU generation failed for fresh variant, keeping default SKU');
            await this.updateVariantDetails(freshVariantId, {
              title: title,
              price: expectedPrice
              // Skip SKU update to keep clean POOL-XXX SKU
            });
          } else {
            await this.updateVariantDetails(freshVariantId, {
              title: title,
              price: expectedPrice,
              sku: sku
            });
          }

          // Add fresh variant to cart (mark as recovery to prevent infinite recursion)
          await new Promise(resolve => setTimeout(resolve, 200)); // Brief delay
          await this.addToCart(freshVariantId, quantity, cleanProperties, productType, expectedPrice, true, fullCalculatorData);

          this.debugLog('✅ Successfully recovered from ghost data with fresh variant');
        } else {
          this.debugLog('⚠️ No fresh variant available, falling back to draft orders');
          throw new Error('Ghost data detected but no clean variants available');
        }
      } else {
        this.debugLog('✅ No ghost data detected - item is clean');
      }

    } catch (error) {
      this.debugLog('❌ Ghost data detection error:', error);
      // Don't throw - let the order proceed even if detection fails
    }
  }

  // Reset variant to clean default state
  async resetVariant(variantId, productType = 'glass') {
    try {
      if (this.debug) this.debugLog(`🔄 Resetting variant ${variantId} to clean state...`);

      this.setVariantState(variantId, this.STATES.PENDING_CLEANUP);

      const defaultState = this.getDefaultVariantState(variantId, productType);

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 12000); // 12 second timeout - increased for resilience

      const response = await fetch(this.updateEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          operation: 'reset',
          productType: productType,
          variant: {
            id: variantId
          }
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Reset failed: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Reset failed');
      }

      // Validate server response for pool variants
      if (this.debug) {
        this.debugLog('📋 Reset server response:', result);
        this.debugLog('✅ Variant reset completed successfully - server confirmed');
      }

      this.setVariantState(variantId, this.STATES.CLEAN);
      if (this.debug) this.debugLog(`✅ Variant ${variantId} reset and validated successfully`);

      // DEFENSIVE LAYER 1: Track reset timestamp to avoid using variant too soon after reset
      this.recentlyResetVariants.set(variantId, Date.now());
      if (this.debug) {
        this.debugLog(`🛡️ Marked variant ${variantId} as recently reset (will skip for ${this.MIN_VARIANT_AGE_MS / 1000}s)`);
      }

      // Track successful variant reset
      this.incrementCounter('variantResets', {
        variantId: variantId,
        productType: productType,
        resetMethod: 'server_endpoint'
      });
      this.logTelemetryEvent('variant_reset_success', {
        variantId: variantId,
        productType: productType,
        resetDuration: Date.now() - this.telemetry.startTime
      });

      return true;


    } catch (error) {
      this.debugLog(`❌ Failed to reset variant ${variantId}:`, error);
      this.setVariantState(variantId, this.STATES.QUARANTINED);

      // Alert admin about quarantined variant
      this.alertQuarantinedVariant(variantId, error.message);

      return false;
    }
  }

  // Validate that variant is actually in expected state
  async validateVariantState(variantId, expectedState) {
    try {
      if (this.debug) this.debugLog(`🔍 Validating variant ${variantId} state...`);

      // For pool variants, rely on the success of the update operation rather than 
      // trying to find them in public product listings
      if (this.poolVariants.includes(variantId)) {
        // Since we're using the variant pool system, we can trust that if the 
        // update operation succeeded, the variant is in the correct state
        if (this.debug) {
          this.debugLog(`✅ Pool variant ${variantId} validation assumed successful (pool system)`);
          this.debugLog(`📝 Expected state:`, expectedState);
        }
        return true;
      }

      // For non-pool variants, use the original validation approach
      const response = await fetch(`/products.json`);
      const data = await response.json();

      // Find the variant across all products
      let actualVariant = null;
      for (const product of data.products) {
        actualVariant = product.variants.find(v => v.id.toString() === variantId);
        if (actualVariant) break;
      }

      if (!actualVariant) {
        if (this.debug) this.debugLog(`❌ Variant ${variantId} not found in public products`);
        return false;
      }

      // Check if variant matches expected state
      const priceMatch = Math.abs(parseFloat(actualVariant.price) - parseFloat(expectedState.price)) < 0.01;
      const titleMatch = actualVariant.title === expectedState.title;
      const skuMatch = actualVariant.sku === expectedState.sku;

      const isValid = priceMatch && titleMatch && skuMatch;

      if (this.debug) {
        this.debugLog(`🔍 Validation results for ${variantId}:`, {
          priceMatch: `${actualVariant.price} === ${expectedState.price} = ${priceMatch}`,
          titleMatch: `"${actualVariant.title}" === "${expectedState.title}" = ${titleMatch}`,
          skuMatch: `"${actualVariant.sku}" === "${expectedState.sku}" = ${skuMatch}`,
          overall: isValid
        });
      }

      return isValid;

    } catch (error) {
      this.debugLog(`❌ Error validating variant ${variantId}:`, error);
      return false;
    }
  }

  // Quarantine variant and alert admin
  alertQuarantinedVariant(variantId, errorMessage) {
    const alertData = {
      variantId: variantId,
      timestamp: new Date().toISOString(),
      error: errorMessage,
      poolType: this.poolVariants === this.glassPoolVariants ? 'glass' : 'brick'
    };

    // Store quarantine info
    const quarantineKey = `quarantined_${variantId}`;
    localStorage.setItem(quarantineKey, JSON.stringify(alertData));

    // Log for admin monitoring
    this.debugLog('🚨 VARIANT QUARANTINED - ADMIN ATTENTION REQUIRED:', alertData);

    // Could also send to external monitoring service here
    // this.sendQuarantineAlert(alertData);
  }

  // Enhanced findAvailableVariant - only returns CLEAN variants
  // Throttled cart fetcher to prevent redundant API calls
  async getThrottledCart() {
    const now = Date.now();

    // Check if we have a valid cache
    if (this.cartCache.data && (now - this.cartCache.timestamp < this.cartCache.TTL)) {
      if (this.debug) this.debugLog('📦 Using cached cart data');
      return this.cartCache.data;
    }

    // Check if a fetch is already in progress
    if (this.cartCache.promise) {
      if (this.debug) this.debugLog('🕒 Waiting for existing cart fetch promise');
      return this.cartCache.promise;
    }

    // Otherwise, fetch fresh data
    if (this.debug) this.debugLog('🌐 Fetching fresh cart data (throttle refresh)');
    this.cartCache.promise = fetch('/cart.js')
      .then(response => {
        if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
        return response.json();
      })
      .then(cart => {
        this.cartCache.data = cart;
        this.cartCache.timestamp = Date.now();
        this.cartCache.promise = null;
        return cart;
      })
      .catch(error => {
        this.cartCache.promise = null;
        throw error;
      });

    return this.cartCache.promise;
  }

  async findAvailableVariant() {
    try {
      if (this.debug) this.debugLog('🔍 Finding available CLEAN variant...');

      // Get current cart to see which variants are in use
      const cart = await this.getThrottledCart();

      // The theme's fetch wrapper returns a synthetic empty cart (no token)
      // during a 429 backoff. Picking from it could overwrite a variant that
      // is already in this cart, so fail to the fallback instead.
      if (!cart || !cart.token || !Array.isArray(cart.items)) {
        throw new Error('CART_UNREADABLE');
      }

      const variantsInCart = cart.items
        .filter(item => this.poolVariants.includes(item.variant_id.toString()))
        .map(item => item.variant_id.toString());

      if (this.debug) {
        this.debugLog('🛒 Variants in cart:', variantsInCart);
      }

      // DEFENSIVE LAYER 1: Clean up old reset timestamps (older than 5 minutes)
      const now = Date.now();
      for (const [variantId, resetTime] of this.recentlyResetVariants.entries()) {
        if (now - resetTime > 300000) { // 5 minutes
          this.recentlyResetVariants.delete(variantId);
        }
      }

      // Get recently updated variants to avoid
      const recentlyUpdated = JSON.parse(localStorage.getItem('recentUpdates') || '{}');

      // Clean up old entries (older than 60 seconds)
      const cleanRecentUpdates = {};
      // Avoid re-writing a variant this browser used in the last 5 minutes:
      // back-to-back writes to one variant raise the stale-storefront risk.
      const RECENT_UPDATE_AVOID_MS = 5 * 60 * 1000;
      Object.keys(recentlyUpdated).forEach(vId => {
        if ((now - recentlyUpdated[vId]) < RECENT_UPDATE_AVOID_MS) {
          cleanRecentUpdates[vId] = recentlyUpdated[vId];
        }
      });
      localStorage.setItem('recentUpdates', JSON.stringify(cleanRecentUpdates));

      // DEFENSIVE LAYER 1: Get list of recently reset variants to avoid
      const recentlyResetIds = [];
      for (const [variantId, resetTime] of this.recentlyResetVariants.entries()) {
        const variantAge = now - resetTime;
        if (variantAge < this.MIN_VARIANT_AGE_MS) {
          recentlyResetIds.push(variantId);
          if (this.debug) {
            this.debugLog(`🛡️ Variant ${variantId} too fresh after reset (${Math.round(variantAge / 1000)}s old, need ${this.MIN_VARIANT_AGE_MS / 1000}s)`);
          }
        }
      }

      // Find variants that are CLEAN, not in cart, and not recently updated/reset
      const cleanVariants = [];

      for (const variantId of this.poolVariants) {
        // Skip if in cart
        if (variantsInCart.includes(variantId)) {
          continue;
        }

        // Skip if recently updated (within RECENT_UPDATE_AVOID_MS)
        if (cleanRecentUpdates[variantId]) {
          if (this.debug) {
            this.debugLog(`Skipping recently updated variant ${variantId} (${Math.round((now - cleanRecentUpdates[variantId]) / 1000)}s ago)`);
          }
          continue;
        }

        // DEFENSIVE LAYER 1: Skip if recently reset (CDN propagation safety)
        if (recentlyResetIds.includes(variantId)) {
          if (this.debug) {
            this.debugLog(`⏭️ Skipping variant ${variantId} (recently reset, awaiting CDN propagation)`);
          }
          continue;
        }

        // DEFENSIVE LAYER 2: Skip if currently being processed (duplicate pool prevention)
        if (this.pendingVariants.has(variantId)) {
          if (this.debug) {
            this.debugLog(`⏭️ Skipping variant ${variantId} (currently being processed by another operation)`);
          }
          continue;
        }

        // Check state
        const state = this.getVariantState(variantId);

        if (state === this.STATES.CLEAN) {
          cleanVariants.push(variantId);
        } else if (state === this.STATES.PENDING_CLEANUP) {
          // Skip variants being cleaned up
          continue;
        } else if (state === this.STATES.QUARANTINED) {
          // Skip quarantined variants
          continue;
        }
      }

      if (this.debug) {
        this.debugLog('✅ Clean variants available:', cleanVariants.length);
      }

      if (cleanVariants.length === 0) {
        // Try to clean up some variants if none are available
        await this.emergencyCleanup();

        // Check again after cleanup
        const retryCleanVariants = this.poolVariants.filter(variantId =>
          !variantsInCart.includes(variantId) &&
          this.getVariantState(variantId) === this.STATES.CLEAN
        );

        if (retryCleanVariants.length === 0) {
          throw new Error('No clean variants available after emergency cleanup');
        }

        // Apply round-robin selection even after emergency cleanup
        const poolType = this.poolVariants === this.glassPoolVariants ? 'glass' : 'brick';
        const storageKey = `lastPoolIndex_${poolType}`;
        const lastUsedIndex = parseInt(localStorage.getItem(storageKey) || '0');
        // Seed to reduce first-visit bias
        let seed = parseInt(localStorage.getItem('poolVisitorSeed') || 'NaN', 10);
        if (isNaN(seed)) { seed = Math.floor(Math.random() * 7); localStorage.setItem('poolVisitorSeed', String(seed)); }
        const nextIndex = (lastUsedIndex + 1 + seed) % retryCleanVariants.length;
        localStorage.setItem(storageKey, nextIndex.toString());

        if (this.debug) {
          this.debugLog(`🚨 Emergency cleanup round-robin: using index ${nextIndex} of ${retryCleanVariants.length} variants`);
        }

        const selectedVariant = retryCleanVariants[nextIndex];
        // CRITICAL: Atomic Reservation
        this.pendingVariants.add(selectedVariant);
        return selectedVariant;
      }

      // Round-robin selection to distribute load across all pools
      const poolType = this.poolVariants === this.glassPoolVariants ? 'glass' : 'brick';
      const storageKey = `lastPoolIndex_${poolType}`;
      const lastUsedIndex = parseInt(localStorage.getItem(storageKey) || '0');
      // Seed to reduce first-visit bias
      let seed = parseInt(localStorage.getItem('poolVisitorSeed') || 'NaN', 10);
      if (isNaN(seed)) { seed = Math.floor(Math.random() * 7); localStorage.setItem('poolVisitorSeed', String(seed)); }
      const nextIndex = (lastUsedIndex + 1 + seed) % cleanVariants.length;
      localStorage.setItem(storageKey, nextIndex.toString());

      const selectedVariantId = cleanVariants[nextIndex];

      // CRITICAL SAFETY: Mark as pending IMMEDIATELY (Atomic Reservation)
      // This prevents race conditions where simultaneous clicks pick the same "CLEAN" variant
      this.pendingVariants.add(selectedVariantId);

      if (this.debug) {
        this.debugLog(`🎯 Selection: Using variant ${selectedVariantId} (Pool Index ${nextIndex})`);
      }

      return selectedVariantId;
    } catch (error) {
      this.debugLog('❌ Error finding available variant:', error);
      throw error;
    }
  }

  // Emergency cleanup - try to reset some variants
  async emergencyCleanup() {
    if (this.debug) this.debugLog('🚨 Emergency cleanup initiated...');

    // Try to clean variants starting from different positions for better distribution
    const startIndex = Math.floor(Math.random() * this.poolVariants.length);
    const variantsToClean = [];

    // Select 10 variants randomly distributed across the pool
    for (let i = 0; i < 10 && i < this.poolVariants.length; i++) {
      const index = (startIndex + i * 5) % this.poolVariants.length;
      variantsToClean.push(this.poolVariants[index]);
    }

    for (const variantId of variantsToClean) {
      const state = this.getVariantState(variantId);

      if (state !== this.STATES.QUARANTINED) {
        try {
          await this.resetVariant(variantId);
        } catch (error) {
          // Continue with next variant if this one fails
          continue;
        }
      }
    }
  }

  // Set product type and switch pools
  setProductType(productType = 'glass') {
    this.productType = productType;
    if (productType === 'brick') {
      this.poolVariants = this.brickPoolVariants;
      if (this.debug) this.debugLog('🧱 Using brick pool variants');
    } else {
      this.poolVariants = this.glassPoolVariants;
      if (this.debug) this.debugLog('🔍 Using glass pool variants');
    }
  }

  // Filter properties for clean cart display and supplier-friendly order emails
  filterCartProperties(properties, productType = 'glass') {
    const cleanProperties = {};
    const accessories = [];

    // Define property display order for cleaner supplier emails
    const priorityOrder = ['Glass Type', 'Brick Type', 'Shape Details', 'Glass Shape', 'Brick Shape',
      'Overall Width', 'Overall Height', 'Width', 'Height', 'Diameter',
      'Height to Cut-off', 'Top Flat Width', 'Corner Radius', 'Thickness', 'Drawing Uploaded'];
    const accessoryKeys = ['Door Rope Kit', 'Glass Tape', 'Gasket'];
    const skipKeys = ['Measurements In', 'Dimensions', 'Area per Unit', 'Total Area', 'Shape', 'Accessory Qty', 'Accessory Choice', 'Drawing Uploaded'];

    // First pass: collect accessories separately
    Object.keys(properties).forEach(key => {
      if (accessoryKeys.includes(key) && properties[key]) {
        accessories.push(properties[key]);
      }
    });

    // Second pass: add properties in priority order
    priorityOrder.forEach(key => {
      if (properties[key] && !accessoryKeys.includes(key)) {
        cleanProperties[key] = properties[key];
      }
    });

    // Third pass: add remaining properties (except skipped ones, internal ones, and accessories)
    Object.keys(properties).forEach(key => {
      // Native Shopify hiding: always allow internal system properties
      if (key === '_View Drawing' || key === '_Spec Image' || key === '_Drawing' ||
        key === '_Expected Price' || key === '_Expected SKU' || key === '_Product Title' ||
        key === '_ClientPatchVersion' || key === '_ValidationSource' || key === '_AddPath' ||
        key === '_Quantity' || key === '_Added At' || key === '_Internal_Shape' || key === '_Internal_Units' ||
        key === '_Internal_Thickness' || key === '_Internal_Type' || key === '_Entered' ||
        key === '_Width' || key === '_Height' ||
        key === '_Fresh Variant' || key === '_Pool Variant ID') {
        cleanProperties[key] = properties[key];
        return;
      }
      if (key.startsWith('_')) return;
      if (skipKeys.includes(key)) return;
      if (accessoryKeys.includes(key)) return;
      if (priorityOrder.includes(key)) return; // Already added

      // Handle Shape conversion for backward compatibility
      if (key === 'Shape') {
        if (productType === 'brick') {
          const brickShapeNames = {
            'square-rectangular': 'Rectangular',
            'shaped': 'Shaped'
          };
          if (!cleanProperties['Brick Shape']) {
            cleanProperties['Brick Shape'] = brickShapeNames[properties[key]] || properties[key];
          }
        } else {
          const glassShapeNames = {
            'square-rectangular': 'Rectangular',
            'arched': 'Arched',
            'half-arch': 'Half Arch',
            'both-top-corners-cut': 'Corner Cut',
            'single-corner-cut': 'Single Corner Cut',
            'all-corners-cut': 'All Corners Cut',
            'sloped-top': 'Sloped Top',
            'circular': 'Circular'
          };
          if (!cleanProperties['Glass Shape']) {
            cleanProperties['Glass Shape'] = glassShapeNames[properties[key]] || properties[key];
          }
        }
        return;
      }

      cleanProperties[key] = properties[key];
    });

    // Add combined accessories at the end (if any)
    if (accessories.length > 0) {
      let kitsCount = 1;
      let isFixedTotal = false;

      // Extract quantity details
      if (properties['Accessory Qty']) {
        const val = properties['Accessory Qty'];
        if (val.includes('Fixed Total')) {
          const match = val.match(/^(\d+)/);
          if (match) {
            kitsCount = parseInt(match[1], 10);
            isFixedTotal = true;
          }
        }
      }

      // Format: "1 x Item (Per piece)" or "3 x Item (Order total)"
      const formattedAccessories = accessories.map(a => {
        const suffix = isFixedTotal ? 'Order total' : 'Per piece';
        return `${kitsCount} x ${a} (${suffix})`;
      });

      cleanProperties['Accessories'] = formattedAccessories.join(', ');
    }

    return cleanProperties;
  }

  // Get dynamic delay based on recent error history
  getDynamicDelay(productType) {
    const errorHistoryKey = `priceErrors_${productType}`;
    const errorHistory = JSON.parse(localStorage.getItem(errorHistoryKey) || '[]');
    const now = Date.now();

    // Clean up old errors (older than 15 minutes)
    const recentErrors = errorHistory.filter(timestamp => now - timestamp < 15 * 60 * 1000);
    localStorage.setItem(errorHistoryKey, JSON.stringify(recentErrors));

    // Determine delay based on recent error count
    if (recentErrors.length === 0) {
      return productType === 'brick' ? 400 : 300; // Fast when stable
    } else if (recentErrors.length <= 2) {
      return productType === 'brick' ? 700 : 500; // Medium when some issues
    } else {
      return productType === 'brick' ? 1200 : 800; // Conservative when problematic
    }
  }

  // Record price error for dynamic delay calculation
  recordPriceError(productType) {
    const errorHistoryKey = `priceErrors_${productType}`;
    const errorHistory = JSON.parse(localStorage.getItem(errorHistoryKey) || '[]');
    errorHistory.push(Date.now());
    localStorage.setItem(errorHistoryKey, JSON.stringify(errorHistory));
  }

  /**
   * Verify that the item just added to the cart has the correct price and SKU.
   * This catches Shopify propagation lag where the Storefront API serves a stale £0.01 price.
   * @param {string} variantId - The variant ID to check
   * @param {number} expectedPrice - The price we expected
   * @param {string|null} expectedSKU - The SKU we expected
   * @param {number} retryCount - Retry attempt for fresh-cart propagation delays
   * @returns {object} { success: boolean, actualPrice: number, lineItemIndex: number }
   */
  async verifyPriceInCart(variantId, expectedPrice, expectedSKU = null, retryCount = 0) {
    try {
      const MAX_RETRIES = 1;
      const RETRY_DELAY_MS = 700;

      // Verification must bypass the normal cart cache because this runs immediately after /cart/add.js.
      const response = await fetch(`/cart.js?_cb=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`Fresh cart fetch failed: ${response.status}`);
      }

      const cart = await response.json();
      this.cartCache.data = cart;
      this.cartCache.timestamp = Date.now();
      this.cartCache.promise = null;

      const expectedPriceFixed = parseFloat(expectedPrice.toFixed(2));
      const expectedSKUString = expectedSKU ? String(expectedSKU) : null;

      // Find the item we just added - try property matching first (most accurate)
      let matchedItem = cart.items.find(item => {
        if (item.variant_id.toString() !== variantId.toString()) return false;
        return item.properties && item.properties['_Expected Price'] === expectedPriceFixed.toFixed(2);
      });

      // Fallback: If property matching failed, find ANY item with this variant ID
      // This handles race conditions where property hasn't propagated yet
      if (!matchedItem) {
        const variantItems = cart.items.filter(item =>
          item.variant_id.toString() === variantId.toString()
        );
        // Take the last one (most recently added)
        if (variantItems.length > 0) {
          matchedItem = variantItems[0];
          console.log('🔎 Using fallback variant matching (Index 0 - Most Recent)');
        }
      }

      if (!matchedItem) {
        if (retryCount < MAX_RETRIES) {
          console.warn('⚠️ Item not found in fresh cart during verification - retrying once');
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
          return this.verifyPriceInCart(variantId, expectedPrice, expectedSKUString, retryCount + 1);
        }

        console.error('🚨 Item not found in fresh cart after add - failing closed');
        return {
          success: false,
          reason: 'item_not_found_after_add',
          actualPrice: null,
          actualSKU: null,
          lineItemIndex: null
        };
      }

      // Check the price. Line item price is in cents.
      const actualPrice = matchedItem.price / 100;
      const isPriceCorrect = Math.abs(actualPrice - expectedPriceFixed) < 0.01;
      const actualSKU = matchedItem.sku ? String(matchedItem.sku) : '';
      const isSKUCorrect = !expectedSKUString || actualSKU === expectedSKUString;

      if (!isPriceCorrect) {
        console.warn('🚨 PRICE MISMATCH DETECTED IN CART:', {
          variantId: variantId,
          expected: expectedPriceFixed,
          actual: actualPrice
        });
      }
      if (!isSKUCorrect) {
        console.warn('🚨 SKU MISMATCH DETECTED IN CART:', {
          variantId: variantId,
          expected: expectedSKUString,
          actual: actualSKU
        });
      }

      return {
        success: isPriceCorrect && isSKUCorrect,
        actualPrice: actualPrice,
        actualSKU: actualSKU,
        reason: isPriceCorrect && isSKUCorrect ? 'verified' : 'cart_item_mismatch',
        mismatch: {
          price: !isPriceCorrect,
          sku: !isSKUCorrect
        },
        lineItemIndex: cart.items.indexOf(matchedItem) + 1 // 1-based index
      };
    } catch (error) {
      console.error('Price verification error:', error);
      return { success: true, reason: 'verification_failed_fetch' }; // Fail open on network error
    }
  }

  /**
   * Remove a contaminated item from the cart
   * @param {number} lineIndex - 1-based index of the line item
   */
  async removeCartItem(lineIndex) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: lineIndex, quantity: 0 })
      });
      if (this.debug) this.debugLog(`🗑️ Removed contaminated item at line ${lineIndex}`);
    } catch (error) {
      console.error('Error removing cart item:', error);
    }
  }

  // Update variant details with retry logic, timeout, and dynamic delays
  async updateVariantDetails(variantId, details, retryCount = 0) {
    const maxRetries = 2;
    const timeoutMs = 12000; // 12 second timeout - increased for slow networks/cold starts

    try {
      if (this.debug) {
        this.debugLog(`📝 Updating variant: ${variantId} (attempt ${retryCount + 1})`);
        this.debugLog('📝 Details:', details);
      }

      const roundedPrice = Number(details.price).toFixed(2);
      const requestBody = {
        variant: {
          id: variantId,
          price: roundedPrice,
          sku: details.sku
        }
      };

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      const response = await fetch(this.updateEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update variant: ${response.status} - ${errorText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Update failed');
      }

      // Additional validation: Wait and verify the price update propagated correctly
      // This is critical for brick calculator to avoid wrong prices in cart
      if (this.debug) {
        this.debugLog('⏳ Waiting for price propagation...');
      }

      // Use dynamic delay based on recent error history
      const productType = this.poolVariants === this.glassPoolVariants ? 'glass' : 'brick';
      const dynamicDelay = this.getDynamicDelay(productType);

      if (this.debug) {
        this.debugLog(`⏳ Using dynamic delay: ${dynamicDelay}ms (based on recent error history)`);
      }

      await new Promise(resolve => setTimeout(resolve, dynamicDelay));

      // Mark variant as recently updated for 60-second avoidance window
      const recentlyUpdated = JSON.parse(localStorage.getItem('recentUpdates') || '{}');
      recentlyUpdated[variantId] = Date.now();
      localStorage.setItem('recentUpdates', JSON.stringify(recentlyUpdated));

      // Diagnostics only (never throws): remember when this write was confirmed
      this.recordPoolWrite(variantId, roundedPrice, details.sku);

      if (this.debug) {
        this.debugLog('✅ Variant update completed with propagation verification');
        this.debugLog(`📝 Marked variant ${variantId} as recently updated`);
      }

      return result;

    } catch (error) {
      // Handle timeout with one retry before failing - improves resilience for transient cold starts
      if (error.name === 'AbortError') {
        this.debugLog('❌ Netlify endpoint timeout - server may be down');
        if (retryCount < maxRetries) {
          this.debugLog(`⚠️ Timeout detected, retrying... (${retryCount + 1}/${maxRetries})`);
          const retryDelay = 1000;
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          return await this.updateVariantDetails(variantId, details, retryCount + 1);
        }
        throw new Error('NETLIFY_TIMEOUT');
      }

      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        this.debugLog('❌ Network error - Netlify may be unavailable');
        if (retryCount < maxRetries) {
          this.debugLog(`⚠️ Network error, retrying... (${retryCount + 1}/${maxRetries})`);
          const retryDelay = retryCount === 0 ? 500 : 1000;
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          return await this.updateVariantDetails(variantId, details, retryCount + 1);
        }
        throw new Error('NETLIFY_UNAVAILABLE');
      }

      if (retryCount < maxRetries) {
        this.debugLog(`⚠️ Update failed, retrying... (${retryCount + 1}/${maxRetries})`);
        const retryDelay = retryCount === 0 ? 300 : 700;
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return await this.updateVariantDetails(variantId, details, retryCount + 1);
      }

      this.debugLog('❌ Error updating variant after all retries:', error);
      throw error;
    }
  }

  /**
   * Verify variant before adding to cart by fetching fresh data from Shopify Storefront API
   * This catches cases where the variant was overwritten by another user/session
   * SMART FAIL-SAFE: Only blocks on CONFIRMED ghost data (SKU/price mismatch), not network issues
   * @param {string} variantId - The variant ID to verify
   * @param {string} productType - 'glass' or 'brick'
   * @param {string} expectedSKU - The SKU we expect to see
   * @param {number} expectedPrice - The price we expect to see
   * @param {number} retryCount - Retry attempt (internal use)
   * @returns {object} { success: boolean, actual: { sku, price }, mismatch: { sku, price } }
   */
  async verifyVariantBeforeCart(variantId, productType, expectedSKU, expectedPrice, retryCount = 0) {
    const MAX_RETRIES = 1; // One retry for transient issues
    const TIMEOUT_MS = 8000; // 8 seconds - realistic timeout
    // CDN propagation backoff delays (ms) - wait for Shopify CDN to catch up after Admin API update
    const PROPAGATION_DELAYS = [0, 800, 1800, 3500]; // Try immediately, then 0.8s, 1.8s, 3.5s
    const startTime = Date.now();

    try {
      // Determine product handle based on type
      const productHandle = productType === 'brick'
        ? 'replacement-stove-bricks-cut-to-size-vermiculite-15mm-20mm-and-25mm-thick'
        : 'cut-to-size-stove-glass';

      if (this.debug) {
        this.debugLog(`🔍 Fetching fresh variant from Shopify (attempt ${retryCount + 1}): /products/${productHandle}.js`);
      }

      // Create abort controller for timeout protection
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

      // Cache-busting: Add timestamp query param AND no-store header to bypass CDN/browser cache
      const cacheBuster = `_cb=${Date.now()}`;
      const fetchUrl = `/products/${productHandle}.js?${cacheBuster}`;

      // Fetch fresh product data from Shopify Storefront API with cache-busting
      const response = await fetch(fetchUrl, {
        signal: controller.signal,
        cache: 'no-store',  // Bypass browser cache
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      clearTimeout(timeoutId);

      const fetchDuration = Date.now() - startTime;

      if (!response.ok) {
        console.warn(`⚠️ Verification fetch failed (${response.status}) after ${fetchDuration}ms`);

        // Log telemetry
        this.logTelemetryEvent('verification_fetch_failed', {
          variantId: variantId,
          status: response.status,
          duration: fetchDuration,
          retryCount: retryCount
        });

        // Retry once for transient issues
        if (retryCount < MAX_RETRIES) {
          console.log('🔄 Retrying verification (network issue)...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          return this.verifyVariantBeforeCart(variantId, productType, expectedSKU, expectedPrice, retryCount + 1);
        }

        // After retry: USE FALLBACK (changed from fail-open to fail-to-fallback)
        console.warn('⚠️ Verification fetch failed - triggering penny fallback for safety');
        this.logTelemetryEvent('verification_fallback_triggered', {
          variantId: variantId,
          reason: 'fetch_failed_after_retry',
          retryCount: retryCount
        });
        return {
          success: 'FALLBACK_REQUIRED',  // Triggers penny-based fallback
          actual: { sku: 'fetch-failed', price: 'fetch-failed' },
          reason: 'Network issue - using penny fallback for correct pricing'
        };
      }

      const productData = await response.json();
      const actualVariant = productData.variants.find(v => v.id.toString() === variantId.toString());

      if (!actualVariant) {
        console.warn(`⚠️ Variant ${variantId} not found in product data after ${fetchDuration}ms`);

        // Log telemetry
        this.logTelemetryEvent('verification_variant_not_found', {
          variantId: variantId,
          duration: fetchDuration,
          retryCount: retryCount,
          totalVariantsInProduct: productData.variants.length
        });

        // Retry once
        if (retryCount < MAX_RETRIES) {
          console.log('🔄 Retrying verification (variant not found)...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          return this.verifyVariantBeforeCart(variantId, productType, expectedSKU, expectedPrice, retryCount + 1);
        }

        // After retry: USE FALLBACK (changed from fail-open to fail-to-fallback)
        console.warn('⚠️ Variant not found after retry - triggering penny fallback for safety');
        this.logTelemetryEvent('verification_fallback_triggered', {
          variantId: variantId,
          reason: 'variant_not_found_after_retry',
          retryCount: retryCount
        });
        return {
          success: 'FALLBACK_REQUIRED',  // Triggers penny-based fallback
          actual: { sku: 'not-found', price: 'not-found' },
          reason: 'Shopify API issue - using penny fallback for correct pricing'
        };
      }

      // WE HAVE ACTUAL VARIANT DATA - NOW CHECK FOR GHOST DATA
      const actualSKU = actualVariant.sku;
      const actualPrice = parseFloat(actualVariant.price) / 100; // Shopify returns price in cents

      const skuMatch = actualSKU === expectedSKU;
      const priceMatch = Math.abs(actualPrice - expectedPrice) < 0.01;

      if (this.debug || retryCount > 0) {
        console.log(`🔍 Verification complete (${fetchDuration}ms):`, {
          variantId: variantId,
          expected: { sku: expectedSKU, price: expectedPrice },
          actual: { sku: actualSKU, price: actualPrice },
          match: skuMatch && priceMatch
        });
      }

      if (skuMatch && priceMatch) {
        // VERIFIED CLEAN!
        if (retryCount > 0) {
          console.log('✅ Verification passed on retry');
        }

        this.logTelemetryEvent('verification_passed', {
          variantId: variantId,
          duration: fetchDuration,
          retryCount: retryCount
        });

        return {
          success: true,
          actual: { sku: actualSKU, price: actualPrice },
          mismatch: { sku: false, price: false }
        };
      } else if (priceMatch && !skuMatch) {
        // PRICE MATCHES BUT SKU IS STALE (CDN lag) - ALLOW ORDER
        // The actual order will have correct SKU from Shopify origin server
        console.warn('⚠️ SKU mismatch (CDN lag) but price matches - proceeding with order:', {
          variantId: variantId,
          expectedSKU: expectedSKU,
          actualSKU: actualSKU,
          price: actualPrice
        });

        this.logTelemetryEvent('verification_sku_lag', {
          variantId: variantId,
          expectedSKU: expectedSKU,
          actualSKU: actualSKU,
          price: actualPrice,
          duration: fetchDuration,
          retryCount: retryCount
        });

        return {
          success: true, // ALLOW ORDER - price is correct, SKU lag is acceptable
          actual: { sku: actualSKU, price: actualPrice },
          mismatch: { sku: true, price: false },
          note: 'SKU mismatch due to CDN lag - order proceeds with correct pricing'
        };
      } else {
        // PRICE MISMATCH DETECTED - But could be CDN propagation lag!
        // Try propagation backoff before declaring true mismatch
        console.warn('⚠️ Price mismatch detected - attempting CDN propagation backoff...', {
          variantId: variantId,
          expected: { sku: expectedSKU, price: expectedPrice },
          actual: { sku: actualSKU, price: actualPrice }
        });

        // Propagation backoff loop - give Shopify CDN time to catch up
        for (let backoffAttempt = 1; backoffAttempt < PROPAGATION_DELAYS.length; backoffAttempt++) {
          const delay = PROPAGATION_DELAYS[backoffAttempt];
          console.log(`⏳ CDN propagation backoff: waiting ${delay}ms (attempt ${backoffAttempt}/${PROPAGATION_DELAYS.length - 1})...`);

          await new Promise(resolve => setTimeout(resolve, delay));

          try {
            // Re-fetch with fresh cache-buster
            const backoffCacheBuster = `_cb=${Date.now()}`;
            const backoffUrl = `/products/${productHandle}.js?${backoffCacheBuster}`;
            const backoffResponse = await fetch(backoffUrl, {
              cache: 'no-store',
              headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache'
              }
            });

            if (backoffResponse.ok) {
              const backoffData = await backoffResponse.json();
              const backoffVariant = backoffData.variants.find(v => v.id.toString() === variantId.toString());

              if (backoffVariant) {
                const backoffSKU = backoffVariant.sku;
                const backoffPrice = parseFloat(backoffVariant.price) / 100;
                const backoffSkuMatch = backoffSKU === expectedSKU;
                const backoffPriceMatch = Math.abs(backoffPrice - expectedPrice) < 0.01;

                console.log(`🔍 Backoff attempt ${backoffAttempt} result:`, {
                  expected: { sku: expectedSKU, price: expectedPrice },
                  actual: { sku: backoffSKU, price: backoffPrice },
                  match: backoffSkuMatch && backoffPriceMatch
                });

                if (backoffSkuMatch && backoffPriceMatch) {
                  // CDN caught up! Verification passed after backoff
                  console.log(`✅ CDN propagation successful after ${delay}ms backoff`);
                  this.logTelemetryEvent('verification_passed_after_backoff', {
                    variantId: variantId,
                    backoffAttempt: backoffAttempt,
                    totalDelay: PROPAGATION_DELAYS.slice(0, backoffAttempt + 1).reduce((a, b) => a + b, 0),
                    duration: Date.now() - startTime
                  });
                  return {
                    success: true,
                    actual: { sku: backoffSKU, price: backoffPrice },
                    mismatch: { sku: false, price: false },
                    note: `CDN propagation succeeded after ${backoffAttempt} backoff attempts`
                  };
                } else if (backoffPriceMatch && !backoffSkuMatch) {
                  // Price now matches, SKU still lagging - that's acceptable
                  console.log(`✅ Price matches after backoff (SKU still lagging - acceptable)`);
                  this.logTelemetryEvent('verification_price_match_after_backoff', {
                    variantId: variantId,
                    backoffAttempt: backoffAttempt,
                    duration: Date.now() - startTime
                  });
                  return {
                    success: true,
                    actual: { sku: backoffSKU, price: backoffPrice },
                    mismatch: { sku: true, price: false },
                    note: `Price matched after backoff, SKU lag acceptable`
                  };
                }
                // Still mismatched - continue backoff loop
              }
            }
          } catch (backoffError) {
            console.warn(`⚠️ Backoff fetch ${backoffAttempt} failed:`, backoffError.message);
            // Continue to next backoff attempt
          }
        }

        // All backoff attempts exhausted - this is a true mismatch (likely ghost data from concurrent user)
        console.error('🚨 PRICE MISMATCH PERSISTS AFTER CDN BACKOFF - BLOCKING ORDER:', {
          variantId: variantId,
          expected: { sku: expectedSKU, price: expectedPrice },
          actual: { sku: actualSKU, price: actualPrice },
          mismatch: { sku: !skuMatch, price: !priceMatch },
          backoffAttempts: PROPAGATION_DELAYS.length - 1,
          totalBackoffTime: PROPAGATION_DELAYS.reduce((a, b) => a + b, 0)
        });

        this.logTelemetryEvent('price_mismatch_blocked_after_backoff', {
          variantId: variantId,
          expected: { sku: expectedSKU, price: expectedPrice },
          actual: { sku: actualSKU, price: actualPrice },
          duration: Date.now() - startTime,
          retryCount: retryCount,
          backoffAttempts: PROPAGATION_DELAYS.length - 1,
          critical: true
        });

        return {
          success: false, // BLOCK ORDER - PRICE MISMATCH persists after backoff
          actual: { sku: actualSKU, price: actualPrice },
          expected: { sku: expectedSKU, price: expectedPrice },
          mismatch: {
            sku: !skuMatch,
            price: !priceMatch
          },
          reason: 'PRICE mismatch persists after CDN backoff - blocking to prevent wrong billing'
        };
      }

    } catch (error) {
      const isTimeout = error.name === 'AbortError';
      const duration = Date.now() - startTime;

      console.warn(`⚠️ Verification ${isTimeout ? 'timeout' : 'error'} (${duration}ms):`, error.message);

      // Log telemetry
      this.logTelemetryEvent('verification_exception', {
        variantId: variantId,
        error: error.message,
        isTimeout: isTimeout,
        duration: duration,
        retryCount: retryCount
      });

      // Retry once
      if (retryCount < MAX_RETRIES) {
        console.log(`🔄 Retrying verification after ${isTimeout ? 'timeout' : 'error'}...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return this.verifyVariantBeforeCart(variantId, productType, expectedSKU, expectedPrice, retryCount + 1);
      }

      // After retry: ALLOW ORDER (network error, not ghost data)
      console.warn('⚠️ Allowing order despite exception (network issue, not ghost data)');
      return {
        success: true, // ALLOW ORDER
        actual: { sku: 'error', price: 'error' },
        error: error.message,
        note: `Network ${isTimeout ? 'timeout' : 'error'} - allowing order to proceed`
      };
    }
  }

  // ---- Diagnostics helpers (never throw, never block the customer flow) ----

  // Record a Netlify-confirmed pool write (memory + sessionStorage, last 20)
  recordPoolWrite(variantId, price, sku) {
    try {
      const now = Date.now();
      this.lastWriteConfirmedAt[variantId] = now;
      const list = JSON.parse(sessionStorage.getItem('sgr_recent_pool_writes') || '[]');
      const arr = Array.isArray(list) ? list : [];
      arr.push({ variantId: variantId, sku: sku || null, price: price, confirmedAt: now });
      sessionStorage.setItem('sgr_recent_pool_writes', JSON.stringify(arr.slice(-20)));
    } catch (e) { /* diagnostics only */ }
  }

  getProductHandle(productType) {
    return productType === 'brick'
      ? 'replacement-stove-bricks-cut-to-size-vermiculite-15mm-20mm-and-25mm-thick'
      : 'cut-to-size-stove-glass';
  }

  // One quick storefront product.js read for a variant. Always resolves.
  async fetchProductJsVariant(productType, variantId, bustCache = true) {
    const out = {};
    let timeoutId = null;
    try {
      const controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), 2500);
      const url = `/products/${this.getProductHandle(productType)}.js${bustCache ? `?_cb=${Date.now()}` : ''}`;
      const res = await fetch(url, { cache: 'no-store', signal: controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const product = await res.json();
      const v = (product.variants || []).find(x => String(x.id) === String(variantId));
      if (v) {
        out.price = Number(v.price) / 100;
        out.sku = v.sku || null;
      } else {
        out.error = 'variant_not_in_product_js';
      }
    } catch (e) {
      out.error = (e && e.message) || String(e);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
    return out;
  }

  // Build diagnostics for a stale-price cart response. Always resolves.
  // Which Shopify edge/region served a storefront response (server-timing
  // header, e.g. edge "LHR", country "GB"). Diagnostics only; never throws.
  readEdgeInfo(response) {
    try {
      const st = response && response.headers ? (response.headers.get('server-timing') || '') : '';
      const pick = (name) => { const m = st.match(new RegExp(name + ';desc="([^"]*)"')); return m ? m[1] : null; };
      return { edge: pick('edge'), country: pick('country'), servedBy: pick('servedBy') };
    } catch (e) {
      return null;
    }
  }

  async buildStaleDiagnostics(variantId, productType, addResult, expectedPricePounds, returnedPricePounds, expectedSku, productJsPromise) {
    const d = {};
    try {
      d.cartEdge = this.lastCartEdge || null;
      d.returnedPrice = Number.isFinite(returnedPricePounds) ? returnedPricePounds : null;
      d.returnedSku = (addResult && addResult.sku) || null;
      d.expectedPrice = Number.isFinite(expectedPricePounds) ? expectedPricePounds : null;
      d.expectedSku = expectedSku || null;
      d.variantId = variantId;
      d.productType = productType;
      const now = Date.now();
      const last = this.lastWriteConfirmedAt[variantId];
      d.msSinceWriteConfirmed = last ? now - last : null;

      d.sameSkuPriceRecentlyWritten = false;
      try {
        const list = JSON.parse(sessionStorage.getItem('sgr_recent_pool_writes') || '[]');
        const wantSku = expectedSku || null;
        const wantPrice = Number(expectedPricePounds).toFixed(2);
        (Array.isArray(list) ? list : []).forEach(w => {
          if (!w || String(w.variantId) === String(variantId)) return;
          if (now - w.confirmedAt > 120000) return;
          if (wantSku && w.sku === wantSku && Number(w.price).toFixed(2) === wantPrice) {
            d.sameSkuPriceRecentlyWritten = true;
            d.sameSkuOtherVariantId = w.variantId;
            d.sameSkuOtherMsAgo = now - w.confirmedAt;
          }
        });
      } catch (e) { /* diagnostics only */ }

      try {
        const pool = productType === 'brick' ? this.brickPoolVariants : this.glassPoolVariants;
        const idx = Array.isArray(pool) ? pool.indexOf(variantId) : -1;
        if (idx >= 0) d.poolIndex = idx + 1;
      } catch (e) { /* diagnostics only */ }
      try { d.userAgent = navigator.userAgent.slice(0, 80); } catch (e) { /* diagnostics only */ }

      const pj = await productJsPromise;
      if (pj) {
        if (pj.error) d.productJsError = pj.error;
        if (pj.price !== undefined) d.productJsPrice = pj.price;
        if (pj.sku !== undefined) d.productJsSku = pj.sku;
      }
    } catch (e) {
      d.diagError = (e && e.message) || String(e);
    }
    return d;
  }

  // Diagnostic only: 5s after fallback starts, re-read product.js and report.
  // Never re-adds to cart and never resets a variant.
  scheduleStaleRecheck(variantId, productType, expectedPrice) {
    try {
      const firstAt = Date.now();
      setTimeout(async () => {
        try {
          const r = await this.fetchProductJsVariant(productType, variantId, false);
          const body = {
            operation: 'report-error',
            error: 'POOL_PRICE_STALE_RECHECK',
            variantId: variantId || 'unknown',
            poolType: productType,
            recheckPrice: r.price !== undefined ? r.price : null,
            recheckSku: r.sku !== undefined ? r.sku : null,
            expectedPrice: expectedPrice,
            msAfterFirst: Date.now() - firstAt
          };
          if (r.error) body.recheckError = r.error;
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 3000);
          await fetch(this.updateEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            signal: controller.signal
          });
          clearTimeout(timeoutId);
        } catch (e) { /* diagnostics only */ }
      }, 5000);
    } catch (e) { /* diagnostics only */ }
  }

  // Add to cart
  // Poll product.js until this variant shows the expected SKU (the storefront
  // updates price first and SKU last), or give up after maxWaitMs. Adding
  // before the storefront catches up returns the variant's previous price.
  // Never throws: the post-add guard remains the safety net.
  async waitForStorefrontSku(variantId, productType, expectedSku, maxWaitMs = 4000) {
    const started = Date.now();
    try {
      const label = document.querySelector('#custom-add-to-cart-text') || document.querySelector('#brick-add-to-cart-text');
      if (label) label.textContent = 'Confirming price...';
    } catch (e) { /* cosmetic only */ }
    try {
      while (Date.now() - started < maxWaitMs) {
        const v = await this.fetchProductJsVariant(productType, variantId);
        if (v && v.sku === expectedSku) {
          this.lastStorefrontWaitMs = Date.now() - started;
          return true;
        }
        await new Promise(resolve => setTimeout(resolve, 400));
      }
    } catch (e) { /* fall through to the add */ }
    this.lastStorefrontWaitMs = Date.now() - started;
    return false;
  }

  // Log-only beacon to the Netlify function (no email, diagnostics only).
  async sendLogOnlyBeacon(body) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      await fetch(this.updateEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operation: 'report-error', ...body }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
    } catch (e) { /* diagnostics only */ }
  }

  // Fallback for a stale pool variant: create a fresh single-use variant at the
  // exact price/SKU and add it. A variant created with the final price is
  // correct in the cart on the first add, but /cart/add.js can take a few
  // seconds to accept a brand-new id (404/422 until then), so poll.
  // Returns the /cart/add.js result, or null on any failure. Never throws.
  async addFreshVariant(poolVariantId, quantity, properties, productType, expectedPrice, fullCalculatorData) {
    const started = Date.now();
    let attempts = 0;
    let newId = null;
    let stopStages = () => {};
    const fail = (reason) => {
      console.error('🚨 FRESH VARIANT FAILED:', reason);
      this.sendLogOnlyBeacon({ error: 'FRESH_VARIANT_FAILED', reason: String(reason), variantId: newId, poolVariantId: poolVariantId });
      return null;
    };
    try {
      const expectedSKU = fullCalculatorData && fullCalculatorData.expectedSKU ? String(fullCalculatorData.expectedSKU) : '';
      const priceNum = Number(expectedPrice);
      if (!expectedSKU || !Number.isFinite(priceNum)) return fail('missing_price_or_sku');

      // Staged button text so a longer wait never looks frozen.
      const label = document.querySelector('#custom-add-to-cart-text') || document.querySelector('#brick-add-to-cart-text');
      const stages = [
        [0, 'Firing up your order...'],
        [3000, 'Cutting your price to size...'],
        [7000, 'Nearly there - stoking the fire...'],
        [11000, 'Still getting it lit - thanks for waiting...']
      ];
      const stageTimers = stages.map(([ms, text]) => setTimeout(() => {
        try { if (label) label.textContent = text; } catch (e) { /* cosmetic only */ }
      }, ms));
      stopStages = () => stageTimers.forEach(clearTimeout);

      // 1. Create the variant (8s timeout)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      let created;
      try {
        const createResp = await fetch(this.updateEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            operation: 'create-fresh-variant',
            productType: productType,
            price: priceNum.toFixed(2),
            sku: expectedSKU
          }),
          signal: controller.signal
        });
        created = await createResp.json().catch(() => null);
        if (!createResp.ok || !created || !created.success || !created.variantId) {
          return fail('create_failed_' + (created && created.error ? created.error : createResp.status));
        }
      } finally {
        clearTimeout(timeoutId);
      }
      newId = String(created.variantId);

      // 2. Traceable properties, then filter like the pool add
      const freshProps = Object.assign({}, properties, {
        '_Fresh Variant': 'yes',
        '_Pool Variant ID': String(poolVariantId)
      });
      // keepProperties: already accepted by Shopify on the first add (upload route)
      const cleanProperties = fullCalculatorData && fullCalculatorData.keepProperties
        ? freshProps
        : this.filterCartProperties(freshProps, productType);

      // 3. Poll /cart/add.js until the new id is accepted (or 12s)
      const pollStart = Date.now();
      let result = null;
      while (Date.now() - pollStart < 12000) {
        attempts++;
        const addController = new AbortController();
        const addTimeout = setTimeout(() => addController.abort(), 6000);
        try {
          const resp = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: Number(newId), quantity: quantity, properties: cleanProperties }),
            signal: addController.signal
          });
          if (resp.ok) {
            this.lastFreshEdge = this.readEdgeInfo(resp);
            result = await resp.json();
            break;
          }
          if (resp.status !== 404 && resp.status !== 422) {
            return fail('add_http_' + resp.status);
          }
        } catch (e) {
          // network blip or abort: treat as not yet available and keep polling
        } finally {
          clearTimeout(addTimeout);
        }
        await new Promise(resolve => setTimeout(resolve, 700));
      }
      if (!result) return fail('add_not_accepted_in_12s');
      const msToAccepted = Date.now() - started;

      // 4. Same price check as the pool add
      const returnedPounds = Number(result.original_price ?? result.price) / 100;
      if (!Number.isFinite(returnedPounds) || Math.abs(returnedPounds - priceNum) >= 0.01) {
        if (result.key) {
          try {
            const removeResp = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: result.key, quantity: 0 })
            });
            if (!removeResp.ok) return fail('fresh_price_wrong_remove_failed_' + removeResp.status);
          } catch (e) {
            return fail('fresh_price_wrong_remove_error');
          }
        } else {
          return fail('fresh_price_wrong_no_key');
        }
        return fail('fresh_price_wrong_' + returnedPounds);
      }

      this.sendLogOnlyBeacon({
        error: 'FRESH_VARIANT_ADDED',
        variantId: newId,
        poolVariantId: poolVariantId,
        msToAccepted: msToAccepted,
        cartEdge: this.lastFreshEdge || null,
        attempts: attempts
      });
      return result;
    } catch (e) {
      return fail('exception_' + (e && e.message ? e.message : e));
    } finally {
      stopStages();
    }
  }

  // Upload route: add the multipart form via fetch so the returned price can be
  // checked. A stale line is removed and re-added on a fresh variant, reusing the
  // file URL Shopify already stored, so the file is not uploaded again.
  // Returns true once a correctly priced line is in the cart. Returns false if the
  // request never reached Shopify (caller then does the native form post).
  // Throws if Shopify refuses the add or a wrong-priced line can't be replaced.
  async addMultipartChecked({ form, file, fileProperty, poolVariantId, quantity, productType, expectedPrice, expectedSKU, priceUnitVariantId }) {
    const priceNum = Number(expectedPrice);
    const body = new FormData(form);
    body.set(`properties[${fileProperty}]`, file, file.name);

    let result = null;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: body,
        signal: controller.signal
      });
      if (!response.ok) throw new Error('MULTIPART_ADD_HTTP_' + response.status);
      this.lastCartEdge = this.readEdgeInfo(response);
      result = await response.json();
    } catch (e) {
      if (String(e && e.message).startsWith('MULTIPART_ADD_HTTP_')) throw e;
      // The reply may have been lost after Shopify accepted the add: look for the
      // line first so a retry can't add it twice.
      try {
        const addedAt = body.get('properties[_Added At]');
        const cart = await (await fetch('/cart.js')).json();
        result = (cart.items || []).find(item =>
          String(item.variant_id) === String(poolVariantId) &&
          item.properties && item.properties['_Added At'] === addedAt) || null;
      } catch (lookupError) { /* treated as not added */ }
      if (!result) return false;
    } finally {
      clearTimeout(timeoutId);
    }

    const returnedPounds = Number(result.original_price ?? result.price) / 100;
    if (Number.isFinite(returnedPounds) && Math.abs(returnedPounds - priceNum) < 0.01) return true;

    // Stale price. Everything below needs the stored file URL.
    const stored = Object.assign({}, result.properties);
    if (!result.key || !stored[fileProperty]) throw new Error('MULTIPART_STALE_NOT_RECOVERABLE');
    const removeResp = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: result.key, quantity: 0 })
    });
    if (!removeResp.ok) throw new Error('MULTIPART_STALE_REMOVE_FAILED_' + removeResp.status);

    const fresh = await this.addFreshVariant(poolVariantId, quantity, stored, productType, priceNum, { expectedSKU: expectedSKU, keepProperties: true });
    if (fresh) {
      this.setVariantState(poolVariantId, this.STATES.IN_USE);
      this.logTelemetryEvent('multipart_fresh_variant_used', {
        poolVariantId: poolVariantId,
        freshVariantId: fresh.variant_id || fresh.id || 'unknown',
        expectedPrice: priceNum.toFixed(2),
        returnedPrice: Number(fresh.original_price ?? fresh.price) / 100
      });
      return true;
    }

    // Same last resort as the other routes: price-unit variant at a scaled quantity.
    stored['_AddPath'] = 'multipart_fallback_penny_pricing';
    const pennyResp = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: Number(priceUnitVariantId), quantity: Math.round(priceNum * 100) * quantity, properties: stored })
    });
    if (!pennyResp.ok) throw new Error('MULTIPART_PENNY_FALLBACK_' + pennyResp.status);
    return true;
  }

  async addToCart(variantId, quantity, properties, productType = 'glass', expectedPrice = null, isRecoveryAttempt = false, fullCalculatorData = null) {
    try {
      // Add ground truth properties for diagnostics and order audit trail.
      if (expectedPrice !== null) {
        properties['_Expected Price'] = Number(expectedPrice).toFixed(2);
      }
      if (fullCalculatorData && fullCalculatorData.expectedSKU) {
        properties['_Expected SKU'] = fullCalculatorData.expectedSKU;
      }

      // CRITICAL: Ensure _Product Title is present for cart fix scripts
      if (!properties['_Product Title']) {
        properties['_Product Title'] = productType === 'brick' ? 'Custom Cut Brick' : 'Custom Cut Stove Glass';
      }

      // Version stamp for diagnosing stale-cache incidents
      properties['_ClientPatchVersion'] = '2026-10-28-fresh-hybrid';
      // Timestamp so cart cleanup can grace-period freshly added lines
      properties['_Added At'] = Date.now().toString();

      const cleanProperties = this.filterCartProperties(properties, productType);

      if (this.debug) {
        this.debugLog('🛒 Adding to cart:', {
          variantId: variantId,
          quantity: quantity,
          cleanProperties: cleanProperties
        });
      }

      // (waitForStorefrontSku and the same-variant retry were removed: live
      // tests showed neither helps. A stale pool price is handled by
      // addFreshVariant below.)

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          properties: cleanProperties
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to add to cart: ${errorText}`);
      }

      this.lastCartEdge = this.readEdgeInfo(response);
      const result = await response.json();

      if (expectedPrice !== null && !isRecoveryAttempt) {
        const expectedPricePounds = Number(expectedPrice);
        const returnedPricePence = Number(result.original_price ?? result.price);
        const returnedPricePounds = Number.isFinite(returnedPricePence) ? returnedPricePence / 100 : NaN;
        const priceMismatch = Number.isFinite(expectedPricePounds) &&
          Number.isFinite(returnedPricePounds) &&
          Math.abs(returnedPricePounds - expectedPricePounds) >= 0.01;
        const pennyPriceLeak = Number.isFinite(returnedPricePounds) && returnedPricePounds <= 0.01;

        if (priceMismatch || pennyPriceLeak) {
          console.error('🚨 POST-ADD PRICE GUARD BLOCKED CART ITEM', {
            variantId: variantId,
            itemKey: result.key,
            expectedPrice: expectedPricePounds,
            returnedPrice: returnedPricePounds,
            returnedSku: result.sku,
            expectedSku: fullCalculatorData && fullCalculatorData.expectedSKU ? fullCalculatorData.expectedSKU : null
          });

          this.logTelemetryEvent('post_add_price_guard_block', {
            variantId: variantId,
            itemKey: result.key || 'unknown',
            expectedPrice: expectedPricePounds.toFixed(2),
            returnedPrice: Number.isFinite(returnedPricePounds) ? returnedPricePounds.toFixed(2) : 'unknown',
            returnedSku: result.sku || 'unknown',
            expectedSku: fullCalculatorData && fullCalculatorData.expectedSKU ? fullCalculatorData.expectedSKU : 'unknown',
            reason: pennyPriceLeak ? 'penny_price_leak' : 'price_mismatch'
          });

          // Diagnostics: start the product.js read in parallel with the removal
          let productJsPromise = null;
          try { productJsPromise = this.fetchProductJsVariant(productType, variantId); } catch (e) { /* diagnostics only */ }

          if (result.key) {
            const removeResponse = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: result.key,
                quantity: 0
              })
            });

            if (!removeResponse.ok) {
              const removeError = await removeResponse.text();
              throw new Error(`POST_ADD_PRICE_GUARD_REMOVE_FAILED: ${removeError}`);
            }
          } else {
            throw new Error('POST_ADD_PRICE_GUARD_NO_ITEM_KEY');
          }

          // The stale line is removed. Create a fresh single-use variant at the
          // exact price (correct in the cart on first add) and add that instead.
          // addFreshVariant never throws; null means it failed.
          const fresh = await this.addFreshVariant(
            variantId, quantity, properties, productType, expectedPrice, fullCalculatorData
          );
          if (fresh) {
            // Mirror the normal pool success path. The fresh variant itself is
            // deliberately NOT tracked in the pool state machine.
            this.setVariantState(variantId, this.STATES.IN_USE);
            this.logTelemetryEvent('post_add_fresh_variant_used', {
              poolVariantId: variantId,
              freshVariantId: fresh.variant_id || fresh.id || 'unknown',
              expectedPrice: Number(expectedPrice).toFixed(2),
              returnedPrice: Number(fresh.original_price ?? fresh.price) / 100
            });
            this.debugLog('✅ Added fresh single-use variant to cart');
            return fresh;
          }

          // Fresh variant failed too: complete the sale via the penny fallback
          // instead of blocking the customer. (REMOVE_FAILED / NO_ITEM_KEY
          // above still block.)
          const staleError = new Error('POOL_PRICE_STALE');
          try {
            staleError.diagnostics = await this.buildStaleDiagnostics(
              variantId, productType, result, expectedPricePounds, returnedPricePounds,
              fullCalculatorData && fullCalculatorData.expectedSKU ? fullCalculatorData.expectedSKU : null,
              productJsPromise
            );
            if (staleError.diagnostics) {
              staleError.diagnostics.retried = false;
              staleError.diagnostics.freshVariantTried = true;
            }
          } catch (e) { /* diagnostics only */ }
          throw staleError;
        }
      }

      // Mark variant as in use
      this.setVariantState(variantId, this.STATES.IN_USE);

      // We only inspect /cart/add.js here. A separate /cart.js read can lag or
      // trigger rate limits, but the add response is the checkout-facing line.
      if (expectedPrice !== null && !isRecoveryAttempt) {
        this.logTelemetryEvent('post_add_cart_price_guard_passed', {
          variantId: variantId,
          expectedPrice: Number(expectedPrice).toFixed(2),
          expectedSKU: fullCalculatorData && fullCalculatorData.expectedSKU ? fullCalculatorData.expectedSKU : null,
          returnedPrice: Number(result.original_price ?? result.price) / 100
        });
      }

      this.debugLog('✅ Added to cart successfully');
      return result;

    } catch (error) {
      this.debugLog('❌ Error adding to cart:', error);
      throw error;
    }
  }

  // Generate titles and SKUs (same as original)
  generateTitle(calculatorData) {
    const { productType, shape, width, height, diameter, thickness, units } = calculatorData;

    // Debug logging to track title generation
    if (this.debug) {
      this.debugLog('🏷️ Generating title with data:', calculatorData);
      this.debugLog('🏷️ Shape value received:', shape);
    }

    if (productType === 'brick') {
      const shapeText = shape === 'shaped' ? 'Shaped' : 'Rectangular';
      return `Cut to Size ${thickness} ${shapeText} Brick ${width}×${height}mm`;
    } else {
      const shapeNames = {
        'square-rectangular': 'Rectangular',
        'arched': 'Arched',
        'half-arch': 'Half Arch',
        'both-top-corners-cut': 'Corner Cut',
        'single-corner-cut': 'Single Corner Cut',
        'all-corners-cut': 'All Corners Cut',
        'sloped-top': 'Sloped Top',
        'circular': 'Circular'
      };

      let title = shapeNames[shape] || 'Custom';

      if (shape === 'circular') {
        title += ` ${diameter}${units} Diameter`;
      } else {
        title += ` ${width}×${height}${units}`;
      }

      title += ` ${thickness} Cut to Size Glass`;
      return title;
    }
  }

  generateSKU(calculatorData) {
    const { productType, shape, width, height, diameter, thickness, units, ropeKit, gasket, inputC, inputD } = calculatorData;

    // Debug SKU generation for problematic cases
    if (this.debug) {
      this.debugLog('🔧 generateSKU input:', {
        productType, shape, width, height, diameter, thickness, units,
        ropeKit: ropeKit, gasket: gasket, inputC, inputD
      });
    }

    // Guard against incomplete data - return null instead of malformed SKU
    if (!shape || !thickness || (!width && !height && !diameter)) {
      this.debugLog('⚠️ generateSKU: Incomplete data detected, returning null', {
        shape: shape, thickness: thickness, width: width, height: height, diameter: diameter
      });
      this.incrementCounter('skuGenerationFailures', {
        reason: 'incomplete_data',
        missingFields: {
          shape: !shape,
          thickness: !thickness,
          dimensions: (!width && !height && !diameter)
        }
      });
      return null;
    }

    // Default units to 'mm' if missing
    const safeUnits = units || 'mm';

    // Helper to normalize fragments for SKU
    const norm = (val) => {
      if (val === undefined || val === null) return '';
      return String(val)
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^A-Za-z0-9-]/g, '-')
        .replace(/-+/g, '-')
        .toUpperCase();
    };

    // Helper to convert measurements to MM for SKU consistency
    const convertToMM = (value, unit) => {
      if (!value || value === 0) return 0;

      if (unit === 'mm') {
        return Math.round(value);
      } else if (unit === 'cm') {
        return Math.round(value * 10);
      } else if (unit === 'in') {
        return Math.round(value * 25.4);
      }
      return Math.round(value);
    };

    if (productType === 'brick') {
      // Only change SKU for custom-shaped (user uploads drawing)
      // Leave corner cuts alone - they use Cloudinary system properly
      // Use CUSTBRICK to distinguish from glass CUSTOM-RECT SKUs
      const shapeCode = shape === 'custom-shaped' ? 'CUSTBRICK' : 'RECT';
      return `BRICK-${norm(thickness)}-${shapeCode}-${width}x${height}`;
    } else {
      const parts = [];

      // Convert all measurements to MM for supplier consistency
      const widthMM = convertToMM(width, safeUnits);
      const heightMM = convertToMM(height, safeUnits);
      const diameterMM = convertToMM(diameter, safeUnits);
      const inputCMM = convertToMM(inputC, safeUnits);
      const inputDMM = convertToMM(inputD, safeUnits);

      // Additional guard: if all measurements convert to 0, return null
      if (widthMM === 0 && heightMM === 0 && diameterMM === 0) {
        this.debugLog('⚠️ generateSKU: All measurements converted to 0, returning null');
        return null;
      }

      // Debug converted measurements
      if (this.debug) {
        this.debugLog('🔧 Converted measurements:', {
          original: { width, height, diameter, inputC, inputD, units },
          converted: { widthMM, heightMM, diameterMM, inputCMM, inputDMM }
        });
      }

      // Shape-specific SKU generation with measurements in MM
      if (shape === 'square-rectangular') {
        parts.push('CUSTOM-RECT');
        parts.push(`W${widthMM}MM-H${heightMM}MMx${norm(thickness)}`);

      } else if (shape === 'arched') {
        parts.push('CUSTOM-ARCHED');
        parts.push(`W${widthMM}MM-H${heightMM}MMx${norm(thickness)}`);

      } else if (shape === 'half-arch') {
        parts.push('CUSTOM-HALF-ARCH');
        parts.push(`W${widthMM}MM-H${heightMM}MMx${norm(thickness)}`);

      } else if (shape === 'both-top-corners-cut') {
        parts.push('CUSTOM-BOTH-TOP-CORNERS-CUT');
        parts.push(`W${widthMM}MM-H${heightMM}MM-HEIGHT-TO-CUT-OFF-${inputCMM}MM-TOP-FLAT-${inputDMM}MMx${norm(thickness)}`);

      } else if (shape === 'single-corner-cut') {
        parts.push('CUSTOM-SINGLE-CORNER-CUT');
        parts.push(`W${widthMM}MM-H${heightMM}MM-HEIGHT-TO-CUT-OFF-${inputCMM}MM-TOP-FLAT-${inputDMM}MMx${norm(thickness)}`);

      } else if (shape === 'all-corners-cut') {
        parts.push('CUSTOM-ALL-CORNERS-CUT');
        parts.push(`W${widthMM}MM-H${heightMM}MM-CORNER-CUT-VERTICAL-${inputCMM}MM-CORNER-CUT-HORIZONTAL-${inputDMM}MMx${norm(thickness)}`);

      } else if (shape === 'sloped-top') {
        parts.push('CUSTOM-SLOPED-TOP');
        parts.push(`BOTTOM-W${widthMM}MM-LEFT-H${heightMM}MM-RIGHT-H${inputCMM}MMx${norm(thickness)}`);

      } else if (shape === 'trapezium') {
        parts.push('CUSTOM-TRAPEZIUM');
        parts.push(`BOTTOMW${widthMM}MM-TOPW${inputCMM}MM-H${heightMM}MMx${norm(thickness)}`);

      } else if (shape === 'circular') {
        parts.push('CUSTOM-CIRCULAR');
        parts.push(`D${diameterMM}MMx${norm(thickness)}`);

      } else {
        // Fallback for unknown shapes
        parts.push('CUSTOM-SHAPE');
        parts.push(`${widthMM}MMx${heightMM}MMx${norm(thickness)}`);
      }

      // Accessories in SKU for supplier visibility
      if (ropeKit && ropeKit.selected) {
        const ropeColor = norm(ropeKit.color || '');
        const ropeDia = ropeKit.diameterMm ? `${parseInt(ropeKit.diameterMm, 10)}MM` : '';

        // Build rope kit part: COLOR-ROPE-KIT-DIAMETER (SOFT prefix only for non-3MM)
        const isSoftRope = ropeDia !== '3MM';
        if (ropeColor && ropeDia) {
          parts.push(isSoftRope ? `SOFT-${ropeColor}-ROPE-KIT-${ropeDia}` : `${ropeColor}-ROPE-KIT-${ropeDia}`);
        } else if (ropeColor) {
          parts.push(`SOFT-${ropeColor}-ROPE-KIT`);
        } else {
          parts.push('ROPE-KIT');
        }
      }

      if (gasket && gasket.selected) {
        const gasketColor = norm(gasket.color || '');
        // Default to 2 meters if length not provided
        const lengthMeters = (gasket.lengthMeters != null && !isNaN(gasket.lengthMeters)) ? Number(gasket.lengthMeters) : 2;

        // Special handling for gaskets with size specifications
        if (gasketColor === 'BLACK') {
          parts.push('10MM-BLACK-GASKET');
          parts.push(`${lengthMeters}-METERS`);
        } else if (gasketColor === 'WHITE') {
          parts.push('12MM-OR-15MM-WHITE-GASKET');
          parts.push(`${lengthMeters}-METERS`);
        } else {
          // Standard gasket handling for other colors
          if (gasketColor) parts.push(gasketColor);
          parts.push('GASKET');
          parts.push(`${lengthMeters}-METERS`);
        }
      }

      // Join with hyphens and ensure no accidental double hyphens
      return parts
        .filter(Boolean)
        .join('-')
        .replace(/-+/g, '-');
    }
  }

  // Enhanced processOrder with forced reset and validation
  async processOrder(calculatorData, fallbackFunction) {
    const {
      itemPrice,
      quantity,
      properties,
      productType = 'glass',
      shape,
      width,
      height,
      diameter,
      thickness,
      units,
      ropeKit,
      gasket,
      inputC = 0,
      inputD = 0
    } = calculatorData;

    if (this.debug) {
      this.debugLog('🚀 Starting Enhanced Variant Pool process...');
      this.debugLog('🚀 Calculator data:', calculatorData);
    }

    // Hard fail-closed on invalid pricing to prevent £0.01/£0.00 orders
    const normalizedPrice = Number(itemPrice);
    if (!Number.isFinite(normalizedPrice) || normalizedPrice <= 0.01) {
      this.logTelemetryEvent('invalid_price_blocked', {
        productType: calculatorData.productType || 'unknown',
        itemPrice: itemPrice,
        normalizedPrice: normalizedPrice
      });
      throw new Error('INVALID_PRICE');
    }

    this.setProductType(productType);

    // Try to find the correct button elements (glass or brick calculator)
    const button = document.querySelector('#custom-add-to-cart-button') || document.querySelector('#brick-add-to-cart-button');
    const buttonText = document.querySelector('#custom-add-to-cart-text') || document.querySelector('#brick-add-to-cart-text');

    if (buttonText) buttonText.textContent = 'Creating Your Order...';
    if (button) button.disabled = true;

    let variantId;
    let singleFlightOwner = null;

    try {
      // Global single-flight lock to prevent duplicate add-to-cart operations from
      // duplicated script initialization or rapid re-entry.
      singleFlightOwner = this.acquireSingleFlightLock(productType);

      // Step 1: Find available CLEAN variant  
      if (this.debug) this.debugLog('🔍 Step 1: Finding available CLEAN variant...');
      variantId = await this.findAvailableVariant(); // Changed to 'let' to allow reassignment in retry logic

      // CRITICAL: Double-check pending state (findAvailableVariant already marked it, but safety first)
      if (this.pendingVariants.has(variantId)) {
        // This is expected now, but we'll continue
      } else {
        this.pendingVariants.add(variantId);
      }

      // Mark variant as being processed immediately to prevent race conditions
      this.setVariantState(variantId, this.STATES.PENDING_CLEANUP);

      // Step 2: Generate product details
      if (this.debug) this.debugLog('📝 Step 2: Generating product details...');
      // Use simple customer-friendly title instead of complex shape-based titles
      const title = productType === 'brick' ? 'Custom Cut Brick' : 'Custom Cut Glass';

      // Extract inputC and inputD from calculatorData
      const { inputC, inputD, supplierSpecUrl } = calculatorData;

      // Always generate descriptive SKU (no character limit issues)
      const sku = this.generateSKU({ productType, shape, width, height, diameter, thickness, units, ropeKit, gasket, inputC, inputD });

      // Guard against null SKU - use safe fallback if generateSKU returns null
      const safeSKU = sku || `CUSTOM-${productType.toUpperCase()}-${Date.now()}`;

      // Inject expected SKU into calculator data for audit trail
      calculatorData.expectedSKU = safeSKU;
      if (!sku) {
        this.debugLog('⚠️ generateSKU returned null, using fallback SKU:', safeSKU);
        this.logTelemetryEvent('sku_fallback_used', {
          productType: productType,
          shape: shape,
          fallbackSKU: safeSKU
        });
      }

      // If we have a Cloudinary URL, we'll add it to cart properties instead of SKU
      let specImageProperty = null;
      if (supplierSpecUrl) {
        specImageProperty = supplierSpecUrl;
        this.incrementCounter('cloudinaryUrls', {
          shape: shape,
          url: supplierSpecUrl
        });
        this.logTelemetryEvent('cloudinary_sku_used', {
          shape: shape,
          url: supplierSpecUrl,
          variantId: variantId,
          method: 'cart_properties'
        });
      }

      // Safety check: only throw if safeSKU itself is unavailable (which shouldn't happen)
      if (!safeSKU) {
        this.debugLog('❌ SKU generation failed critically in processOrder, triggering fallback');
        throw new Error('SKU generation failed - incomplete calculator data');
      }

      if (buttonText) buttonText.textContent = 'Updating Product...';

      // Step 3: SAFE UPDATE - update to customer details with validation
      if (this.debug) this.debugLog('📡 Step 3: Safe update to customer details...');

      // NEW: Client-side pricing validation
      // This prevents accidental bugs from sending invalid prices to the server
      try {
        this.validatePricing(itemPrice, thickness, { width, height, diameter, shape });
      } catch (validationError) {
        console.error('🚨 PRICING VALIDATION FAILED:', validationError.message);
        this.logTelemetryEvent('pricing_validation_failed', {
          error: validationError.message,
          price: itemPrice,
          thickness: thickness,
          dimensions: { width, height, diameter }
        });
        throw validationError; // Block the order
      }

      // Directly update to customer details (server will overwrite price+sku atomically)
      // Webhook will reset variant post-purchase to default title/price/sku
      // IMPORTANT: Netlify now returns validated SKU/price after read-after-write verification
      const netlifyResult = await this.updateVariantDetails(variantId, {
        title: title,
        price: itemPrice,
        sku: safeSKU
      });

      // Step 3.5: TRUST SERVER VALIDATION (No slow CDN verification needed!)
      // Netlify has already done read-after-write validation via Admin API (authoritative source)
      // The Admin API response is the ground truth - no CDN lag issues
      if (this.debug) this.debugLog('🔍 Step 3.5: Using server-validated data (no CDN fetch needed)');

      const expectedSKU = safeSKU;
      const expectedPrice = parseFloat(itemPrice.toFixed(2));

      // Build verification result from Netlify's validated response
      // This is much faster than fetching from CDN which has propagation lag
      let verificationResult;

      if (netlifyResult && netlifyResult.validated) {
        // Server confirmed the update via Admin API read-after-write
        const serverSKU = netlifyResult.validated.sku;
        const serverPrice = netlifyResult.validated.price;

        const skuMatch = serverSKU === expectedSKU;
        const priceMatch = Math.abs(serverPrice - expectedPrice) < 0.01;

        if (skuMatch && priceMatch) {
          console.log('✅ SERVER VALIDATION PASSED (Admin API read-after-write)', {
            variantId: variantId,
            sku: serverSKU,
            price: serverPrice
          });
          verificationResult = {
            success: true,
            actual: { sku: serverSKU, price: serverPrice },
            source: 'netlify_validated'
          };
        } else {
          // This should be very rare - server says it updated but values don't match what we sent
          console.warn('⚠️ Server validation mismatch (unexpected):', {
            expected: { sku: expectedSKU, price: expectedPrice },
            server: { sku: serverSKU, price: serverPrice },
            skuMatch: skuMatch,
            priceMatch: priceMatch,
            priceDiff: Math.abs(serverPrice - expectedPrice),
            variantId: variantId
          });

          // Log to telemetry for monitoring
          this.logTelemetryEvent('server_validation_mismatch_details', {
            variantId: variantId,
            expectedSKU: expectedSKU,
            serverSKU: serverSKU,
            expectedPrice: expectedPrice,
            serverPrice: serverPrice,
            skuMatch: skuMatch,
            priceMatch: priceMatch
          });

          verificationResult = {
            success: false,
            actual: { sku: serverSKU, price: serverPrice },
            expected: { sku: expectedSKU, price: expectedPrice },
            source: 'netlify_mismatch'
          };
        }
      } else {
        // Older Netlify response format without validated field - fall back to trusting success
        console.log('✅ SERVER UPDATE SUCCEEDED (legacy response format)', { variantId });
        verificationResult = {
          success: true,
          actual: { sku: expectedSKU, price: expectedPrice },
          source: 'netlify_success_legacy'
        };
      }

      // NEW: Check for FALLBACK_REQUIRED - verification failed due to network issues
      // Instead of proceeding with potentially wrong pricing, use penny-based fallback
      if (verificationResult.success === 'FALLBACK_REQUIRED') {
        console.warn('⚠️ FALLBACK_REQUIRED - Using penny-based fallback for correct pricing');
        this.logTelemetryEvent('fallback_triggered', {
          variantId: variantId,
          productType: productType,
          reason: verificationResult.reason,
          expectedPrice: expectedPrice
        });

        // Release the pending variant since we're not using it
        this.pendingVariants.delete(variantId);
        this.setVariantState(variantId, this.STATES.CLEAN);

        // Reset button state
        if (buttonText) buttonText.textContent = 'Processing (Fallback)...';

        // Call the fallback function passed from calculator
        if (typeof fallbackFunction === 'function') {
          return fallbackFunction();
        } else {
          throw new Error('Fallback function not available');
        }
      }

      if (!verificationResult.success) {
        // This should be very rare with server validation - only happens if Netlify returns
        // different values than what we sent (possible race condition with another user)
        console.error('🚨 SERVER VALIDATION MISMATCH', verificationResult);

        // Quarantine the mismatched variant
        this.setVariantState(variantId, this.STATES.QUARANTINED);

        // Log to telemetry
        this.logTelemetryEvent('server_validation_failed', {
          variantId: variantId,
          productType: productType,
          expected: { sku: expectedSKU, price: expectedPrice },
          actual: verificationResult.actual,
          source: verificationResult.source
        });

        // Retry with fresh variant - server validation should succeed
        if (this.debug) this.debugLog('🔄 Retrying with fresh variant due to server validation mismatch...');
        const freshVariantId = await this.findAvailableVariant(productType);

        // Update the fresh variant and get server validation
        const retryNetlifyResult = await this.updateVariantDetails(freshVariantId, {
          title: title,
          price: itemPrice,
          sku: safeSKU
        });

        // Build retry verification from server response (no CDN fetch needed)
        let retryVerification;
        if (retryNetlifyResult && retryNetlifyResult.validated) {
          const serverSKU = retryNetlifyResult.validated.sku;
          const serverPrice = retryNetlifyResult.validated.price;
          const skuMatch = serverSKU === expectedSKU;
          const priceMatch = Math.abs(serverPrice - expectedPrice) < 0.01;

          retryVerification = {
            success: skuMatch && priceMatch,
            actual: { sku: serverSKU, price: serverPrice }
          };
        } else {
          // Legacy response - trust success
          retryVerification = {
            success: true,
            actual: { sku: expectedSKU, price: expectedPrice }
          };
        }

        if (!retryVerification.success) {
          console.error('🚨 SERVER VALIDATION FAILED ON RETRY - Using fallback');
          // Use fallback instead of blocking completely
          if (typeof fallbackFunction === 'function') {
            this.pendingVariants.delete(freshVariantId);
            this.setVariantState(freshVariantId, this.STATES.CLEAN);
            if (buttonText) buttonText.textContent = 'Processing (Fallback)...';
            return fallbackFunction();
          } else {
            throw new Error(`Server validation failed twice: Expected SKU ${expectedSKU} but got ${retryVerification.actual?.sku || 'unknown'}`);
          }
        }

        // Release old pending variant before switching to the fresh one.
        this.pendingVariants.delete(variantId);

        // Use fresh variant
        variantId = freshVariantId;
        // Pre-cart guard must check the retry's values, not the first (failed) attempt's.
        verificationResult = { success: true, actual: retryVerification.actual, source: 'netlify_validated' };
        console.log('✅ Retry server validation passed, using fresh variant', freshVariantId);
      } else {
        console.log('✅ SERVER VALIDATION PASSED', {
          variantId: variantId,
          sku: verificationResult.actual.sku,
          price: verificationResult.actual.price,
          source: verificationResult.source
        });
      }

      // Step 4: Add to cart after Admin API read-after-write validation.
      // Avoid public cart verification here because it can lag the Admin API update.
      if (this.debug) this.debugLog('🛒 Step 4: Adding to cart...');

      // Add Cloudinary URL to properties if available (with cleaner labeling for supplier)
      if (specImageProperty) {
        // Hidden in cart, visible in admin/emails
        properties['_View Drawing'] = specImageProperty;
        this.debugLog('📷 Added Cloudinary URL to cart properties (prefixed):', specImageProperty);

        // Build human-readable shape description for suppliers (no letter references)
        const width = calculatorData.width || 0;
        const height = calculatorData.height || 0;
        const thickness = calculatorData.thickness || '4mm';
        const inputC = calculatorData.inputC || 0;
        const inputD = calculatorData.inputD || 0;
        const diameter = calculatorData.diameter || 0;
        const shape = calculatorData.shape;

        let shapeDescription = '';

        switch (shape) {
          case 'square-rectangular':
            shapeDescription = `Rectangle`;
            break;

          case 'both-top-corners-cut':
            shapeDescription = `Both Top Corners Cut`;
            break;

          case 'single-corner-cut':
            shapeDescription = `Single Corner Cut`;
            break;

          case 'all-corners-cut':
            shapeDescription = `All Corners Cut`;
            break;

          case 'arched':
            shapeDescription = `Arched Top`;
            break;

          case 'half-arch':
            shapeDescription = `Half Arch`;
            break;

          case 'sloped-top':
            shapeDescription = `Sloped Top`;
            break;

          case 'circular':
            shapeDescription = `Circular`;
            break;

          default:
            // Fallback for unknown shapes
            shapeDescription = shape.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
        }

        properties['Shape Details'] = shapeDescription;
      }

      // CRITICAL SAFETY CHECK: Block cart add if Netlify update wasn't confirmed
      // This prevents £0.01 orders when Netlify request fails silently (network issues, timeouts)
      if (!netlifyResult || !netlifyResult.success) {
        console.error('🚨 PRE-CART SAFETY CHECK FAILED - Netlify update not confirmed', {
          netlifyResult: netlifyResult,
          variantId: variantId,
          expectedPrice: itemPrice,
          expectedSKU: safeSKU
        });

        this.logTelemetryEvent('pre_cart_safety_block', {
          variantId: variantId,
          productType: productType,
          reason: 'netlify_update_not_confirmed',
          netlifyResult: netlifyResult ? JSON.stringify(netlifyResult) : 'null'
        });

        throw new Error('PRE_CART_SAFETY_BLOCK');
      }

      // HARD GUARD: Never add to cart if server-validated state looks like an unconfigured pool variant
      // or penny price while a custom SKU was expected.
      const expectedCustomSku = typeof safeSKU === 'string' ? safeSKU : '';
      const expectedCustom = expectedCustomSku.startsWith('CUSTOM-');
      const verifiedSku = (verificationResult && verificationResult.actual && verificationResult.actual.sku)
        ? String(verificationResult.actual.sku)
        : '';
      const verifiedPrice = Number(
        verificationResult && verificationResult.actual ? verificationResult.actual.price : NaN
      );
      const upperVerifiedSku = verifiedSku.toUpperCase();
      const looksLikePoolSku = /^POOL-\d{3}$/.test(upperVerifiedSku) || /^BRICK-POOL-\d+/.test(upperVerifiedSku);
      const looksLikePennyPrice = Number.isFinite(verifiedPrice) && verifiedPrice <= 0.01;
      const missingValidatedPayload = expectedCustom && (!netlifyResult || !netlifyResult.validated);

      if ((expectedCustom && (looksLikePoolSku || looksLikePennyPrice)) || missingValidatedPayload) {
        this.logTelemetryEvent('pre_cart_pool_guard_block', {
          variantId: variantId,
          expectedSKU: expectedCustomSku,
          verifiedSKU: verifiedSku || 'unknown',
          verifiedPrice: Number.isFinite(verifiedPrice) ? verifiedPrice : 'unknown',
          missingValidatedPayload: missingValidatedPayload
        });
        throw new Error('PRE_CART_POOL_GUARD_BLOCK');
      }

      // Diagnostic: record which validation path was used and what code path reached addToCart
      properties['_ValidationSource'] = verificationResult.source || 'unknown';
      properties['_AddPath'] = 'processOrder';

      await this.addToCart(variantId, quantity, properties, productType, itemPrice, false, calculatorData);

      // Mark variant as CLEAN immediately after successful cart add so that
      // cleanupAbandonedVariants does not treat it as "stuck" and reset it
      // while the customer is still browsing before checkout.
      // The orders/create webhook will handle the actual reset after purchase.
      this.setVariantState(variantId, this.STATES.CLEAN);

      // Success!
      if (this.debug) this.debugLog('✅ Success! Redirecting to cart...');
      if (buttonText) buttonText.textContent = 'Added to Cart!';

      // Track successful order
      this.incrementCounter('successfulOrders', {
        variantId: variantId,
        productType: productType,
        shape: shape,
        usedCloudinaryUrl: !!supplierSpecUrl,
        price: itemPrice
      });
      this.logTelemetryEvent('order_success', {
        variantId: variantId,
        productType: productType,
        finalSKU: safeSKU,
        sessionDuration: Date.now() - this.telemetry.startTime
      });

      setTimeout(() => {
        window.location.href = '/cart';
      }, 500);

    } catch (error) {
      this.debugLog('❌ Enhanced variant pool failed:', error);
      console.error('❌ DETAILED ERROR INFO:', {
        error: error,
        message: error.message,
        stack: error.stack,
        step: 'Unknown - check which step failed above'
      });

      // Track failed order
      this.incrementCounter('failedOrders', {
        productType: productType,
        shape: shape,
        errorType: error.name,
        errorMessage: error.message
      });
      this.logTelemetryEvent('order_failure', {
        productType: productType,
        errorType: error.name,
        errorMessage: error.message,
        calculatorData: calculatorData,
        sessionDuration: Date.now() - this.telemetry.startTime
      });

      if (this.debug) {
        this.debugLog('🔄 Attempting fallback to original method...');
      }

      const blockFallbackErrors = new Set([
        'INVALID_PRICE',
        'PRE_CART_SAFETY_BLOCK',
        'PRE_CART_POOL_GUARD_BLOCK',
        'ORDER_IN_FLIGHT'
      ]);

      if (blockFallbackErrors.has(error.message) || String(error.message || '').startsWith('POST_ADD_PRICE_GUARD')) {
        if (buttonText) buttonText.textContent = 'Add to Cart';
        if (button) button.disabled = false;
        if (error.message === 'ORDER_IN_FLIGHT') {
          alert('Order is already being processed. Please wait a moment.');
        } else {
          alert('Pricing error detected. Please recalculate and try again.');
        }
        // Let the calculator reset its own in-flight UI (otherwise the
        // "Finalizing..." timer can leave the button stuck until reload).
        try {
          document.dispatchEvent(new CustomEvent('sgr-order-blocked', {
            detail: { message: error.message }
          }));
        } catch (e) { /* non-critical */ }
        return;
      }

      if (this.fallbackToCart && fallbackFunction) {
        this.debugLog('Falling back to original cart method...');
        if (buttonText) buttonText.textContent = 'Using Fallback...';

        // Track fallback usage
        this.incrementCounter('fallbacks', {
          productType: productType,
          fallbackMethod: 'original_cart',
          errorType: error.name
        });
        this.logTelemetryEvent('fallback_used', {
          fallbackMethod: 'original_cart',
          errorType: error.name,
          errorMessage: error.message,
          productType: productType
        });

        // Send fallback beacon for monitoring
        this.sendFallbackBeacon({
          error: error.message,
          poolType: this.poolVariants === this.glassPoolVariants ? 'glass' : 'brick',
          variantId: variantId,
          fallbackMethod: 'original_cart',
          calculatorData: {
            ...calculatorData,
            expectedPrice: itemPrice,
            expectedSKU: calculatorData.expectedSKU || null,
            ...(error.message === 'POOL_PRICE_STALE' && error.diagnostics ? {
              actualPrice: error.diagnostics.returnedPrice,
              actualSKU: error.diagnostics.returnedSku
            } : {})
          },
          ...(error.message === 'POOL_PRICE_STALE' ? { diagnostics: error.diagnostics || null } : {})
        });

        if (error.message === 'POOL_PRICE_STALE') {
          this.scheduleStaleRecheck(variantId, productType, itemPrice);
        }

        fallbackFunction();
      } else {
        if (buttonText) buttonText.textContent = 'Error!';
        if (button) button.disabled = false;

        setTimeout(() => {
          if (buttonText) buttonText.textContent = 'Add to Cart';
        }, 2000);

        alert('Unable to process order. Please try again.');
      }
    } finally {
      // CRITICAL: Always remove from pending set, even on error or fallback
      // This ensures the variant becomes available again for next customer
      if (typeof variantId !== 'undefined' && variantId) {
        this.pendingVariants.delete(variantId);
        if (this.debug) {
          this.debugLog(`🔓 Released variant ${variantId} from pending state`);
        }
      }

      // Always release global single-flight lock
      this.releaseSingleFlightLock(singleFlightOwner);
    }
  }

  // Send fallback beacon to server for monitoring
  async sendFallbackBeacon(data) {
    try {
      const beaconData = {
        operation: 'report-error',
        error: data.error,
        poolType: data.poolType,
        variantId: data.variantId || 'unknown',
        shape: data.calculatorData?.shape || 'unknown',
        dimensions: {
          width: data.calculatorData?.width || 0,
          height: data.calculatorData?.height || 0,
          thickness: data.calculatorData?.thickness || 'unknown',
          quantity: data.calculatorData?.quantity || 1,
          expectedPrice: data.calculatorData?.expectedPrice || 'unknown',
          expectedSKU: data.calculatorData?.expectedSKU || 'unknown',
          actualPrice: data.calculatorData?.actualPrice || 'unknown',
          actualSKU: data.calculatorData?.actualSKU || 'unknown'
        },
        fallbackMethod: data.fallbackMethod,
        ...(data.diagnostics ? { diagnostics: data.diagnostics } : {}),
        cartUrl: window.location.href,
        timestamp: new Date().toISOString()
      };

      // Send beacon (non-blocking, short timeout)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

      await fetch(this.updateEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(beaconData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (this.debug) {
        this.debugLog('📊 Fallback beacon sent successfully');
      }
    } catch (beaconError) {
      // Don't fail the main flow if beacon fails
      if (this.debug) {
        this.debugLog('⚠️ Failed to send fallback beacon:', beaconError);
      }
    }
  }

  // Get pool health status
  getPoolHealth() {
    const health = {
      total: this.poolVariants.length,
      clean: 0,
      inUse: 0,
      pendingCleanup: 0,
      quarantined: 0
    };

    this.poolVariants.forEach(variantId => {
      const state = this.getVariantState(variantId);
      switch (state) {
        case this.STATES.CLEAN:
          health.clean++;
          break;
        case this.STATES.IN_USE:
          health.inUse++;
          break;
        case this.STATES.PENDING_CLEANUP:
          health.pendingCleanup++;
          break;
        case this.STATES.QUARANTINED:
          health.quarantined++;
          break;
      }
    });

    return health;
  }
}

// Shared recovery helpers for calculator pages (glass + brick restore sizing
// from an expired pool cart line without retyping). Calculators call
// window.SGRRecovery.consumeRestore() on start.
window.SGRRecovery = (function () {
  const RECOVERY_KEY = 'sgr_expired_recovery_v1';
  const RESTORE_KEY = 'sgr_restore_request_v1';

  function readStore() {
    try { return JSON.parse(localStorage.getItem(RECOVERY_KEY) || '[]'); }
    catch (e) { return []; }
  }

  function parseNum(str) {
    if (str === undefined || str === null) return 0;
    const m = String(str).match(/[\d.]+/);
    return m ? parseFloat(m[0]) : 0;
  }

  function pickProp(props, candidates) {
    for (const k of candidates) {
      if (props[k] !== undefined && props[k] !== null && String(props[k]).trim() !== '') return String(props[k]);
    }
    // Fuzzy fallback: key contains candidate text
    const keys = Object.keys(props);
    for (const c of candidates) {
      const hit = keys.find(k => k.toLowerCase().includes(c.toLowerCase()));
      if (hit && String(props[hit]).trim() !== '') return String(props[hit]);
    }
    return '';
  }

  function toSpec(snap) {
    const p = (snap && snap.properties) || {};
    const glassType = p['Glass Type'] || p['Brick Type'] || '';
    const thickMatch = (glassType + ' ' + (p['Thickness'] || '')).match(/(\d+)\s*mm/i);
    const spec = {
      productType: snap.productType || 'glass',
      shape: p['_Internal_Shape'] || p['_Internal shape'] || '',
      units: p['_Internal_Units'] || p['_Internal units'] || 'mm',
      width: parseNum(pickProp(p, ['Overall Width', 'Width', 'Bottom Width'])),
      height: parseNum(pickProp(p, ['Overall Height', 'Height', 'Left Height'])),
      inputC: parseNum(pickProp(p, ['Top Width', 'Height to Cut-off', 'Height to Cut-Off', 'Corner Cut Vertical', 'Right Height', 'Corner Cut C', 'Input C'])),
      inputD: parseNum(pickProp(p, ['Top Flat Width', 'Top Flat', 'Corner Cut Horizontal', 'Corner Cut D', 'Input D'])),
      diameter: parseNum(pickProp(p, ['Diameter'])),
      thickness: thickMatch ? thickMatch[1] + 'mm' : (p['Thickness'] || ''),
      quantity: parseInt(p['_Quantity'] || p['Quantity'] || snap.quantity || 1, 10) || 1
    };
    // Brick is always mm
    if (spec.productType === 'brick') spec.units = 'mm';
    return spec;
  }

  return {
    consumeRestore: function () {
      try {
        const raw = localStorage.getItem(RESTORE_KEY);
        if (!raw) return null;
        localStorage.removeItem(RESTORE_KEY);
        const snap = JSON.parse(raw);
        return { snapshot: snap, spec: toSpec(snap) };
      } catch (e) { return null; }
    },
    peekRestore: function () {
      try {
        const raw = localStorage.getItem(RESTORE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) { return null; }
    }
  };
})();

// Export for calculator
window.SimpleVariantPool = SimpleVariantPool;
